/**
 * Index Module
 * Encapsulates all functionality for the main index page,
 * primarily managing the Collection Browser.
 */
const indexModule = (function () {
  //#region Parent dependencies
  let _parent;
  let SalesToolkit;
  //#endregion
  //#region Module-level Variables
  let treeViewLargeCols, treeViewMediumCols, treeViewSmallCols;
  let main, tree;

  let searchBox, searchClear;
  let treeViewHiddenWhenSearchBoxOpened;
  let pluginBackEnabled = false;
  let executeSearchTimeout = null;
  const executeSearchDelayInMilliseconds = 150;

  let isTreeReadyForRepopulation = false;
  let repopulationScheduled = false;

  let callLibraryReadyTimeout = null;
  let hideSyncModalTimeout = null;
  let checkInitialSyncTimeout = null;
  let pressTimer = null;
  let FAVOURITES_HEART_FULL,
    FAVOURITES_HEART_EMPTY,
    SHOPPING_CART_FULL,
    SHOPPING_CART_EMPTY;
  let activeFilter = null;
  let salestoolkitFirstTimeSyncInterval = null;

  let keepSearchTagsOpen = false;
  let loadedHeaderId = "";
  let collectionBrowser;
  let treeView;

  let allFilteredDropdowns = [];
  let allowFiltersMultiSelection = false;
  let hiddenFiltersCleanupRan = false;
  let librarySyncInProgress = false;

  let salestoolkitCollectionViewTracking = null;
  let _updateTickerCallback;

  let _eventHandlerMap = null;
  let _calViewBridge = null;
  let _viewCollectionId = null;
  let _viewCollectionLoaded = false;
  //#endregion

  //#region Layout Management
  /**
   * Scrolls the main content area to the top.
   */
  function _scrollToTop() {
    const mainDiv = $("#main-div-center");
    const animate = SalesToolkit.Theme.getBooleanProperty(
      "animateScrollToTopOnCollectionChange",
      true
    );

    setTimeout(() => {
      if (animate) {
        mainDiv.animate({ scrollTop: "0px" });
      } else {
        mainDiv.scrollTop(0);
      }
    }, 0);
  }
  /**
   * Removes all Bootstrap 'col-*' classes from an element.
   * @param {HTMLElement} element - The DOM element to clean.
   */
  function _removeAllColCSS(element) {
    if (!element) return;

    const $element = $(element); // Use const for jQuery object as it's not reassigned
    const classes = $element.attr("class");

    if (!classes) {
      // Handle cases where the element might not have a class attribute
      return;
    }

    const classList = classes.split(" ");
    const newClassList = []; // Use const for the array, as the reference won't change

    for (let i = 0; i < classList.length; i++) {
      // Use let for loop counter
      if (!classList[i].startsWith("col-")) {
        newClassList.push(classList[i]);
      }
    }

    $element.removeClass().addClass(newClassList.join(" "));
  }

  /**
   * Sets up the responsive column layout for the main content and tree view panels.
   */
  function _setupColumns() {
    if (!tree || !main) return;

    _removeAllColCSS(tree);
    $(tree).addClass("col-lg-" + treeViewLargeCols);
    $(tree).addClass("col-md-" + treeViewMediumCols);
    $(tree).addClass("col-sm-" + treeViewSmallCols);

    _removeAllColCSS(main);
    let mainLGSize = 12;
    let mainMDSize = 12;
    let mainSMSize = 12;

    if (!localStorage.hideTree) {
      mainLGSize -= treeViewLargeCols;
      mainMDSize -= treeViewMediumCols;
      mainSMSize -= treeViewSmallCols;
    }
    $(main).addClass("col-lg-" + mainLGSize);
    $(main).addClass("col-md-" + mainMDSize);
    $(main).addClass("col-sm-" + mainSMSize);
  }

  /**
   * Toggles the visibility of the tree view panel and adjusts the main content width.
   */
  function _toggleTree() {
    if (!tree || !main) return;
    let mainLGSize = 12;
    let mainMDSize = 12;
    let mainSMSize = 12;

    if (tree.style.display === "none") {
      _parent.trackAppClick(
        $("#tree-toggler"),
        "#tree-toggler",
        "trackingTreeViewToggleShow",
        SalesToolkit.Translation.get(
          "trackingTreeViewToggleShow",
          "trackingTreeViewToggleShow"
        )
      );
      delete localStorage["hideTree"];
      tree.style.display = "block";
      $("body").toggleClass("showingTreeView", true);
      $("body").toggleClass("treeviewclosed", false);
      mainLGSize -= treeViewLargeCols;
      mainMDSize -= treeViewMediumCols;
      mainSMSize -= treeViewSmallCols;
    } else {
      _parent.trackAppClick(
        $("#tree-toggler"),
        "#tree-toggler",
        "trackingTreeViewToggleHide",
        SalesToolkit.Translation.get(
          "trackingTreeViewToggleHide",
          "trackingTreeViewToggleHide"
        )
      );
      localStorage.hideTree = true;
      tree.style.display = "none";
      main.style.display = "block";
      $("body").toggleClass("showingTreeView", false);
      $("body").toggleClass("treeviewclosed", true);
    }

    _removeAllColCSS(main);
    $(main).addClass("col-lg-" + mainLGSize);
    $(main).addClass("col-md-" + mainMDSize);
    $(main).addClass("col-sm-" + mainSMSize);
  }
  //#endregion

  //#region Pagination & Rendering
  /**
   * Updates pagination links and processes thumbnails and other elements for the current view.
   */
  function _updateURLsAndPageButtonLinks() {
    if (
      collectionBrowser.totalPages <= 1 &&
      (!collectionBrowser.searchTerm ||
        collectionBrowser.searchTerm.length < 2) &&
      !collectionBrowser.filtered
    ) {
      $(".collection-browser-pagination").removeClass("shown");
    } else {
      $(".collection-browser-pagination .page-button").each(function () {
        if ($(this).attr("data-page-number") > collectionBrowser.totalPages) {
          $(this).remove();
        }
        if ($(this).attr("data-page-number") == collectionBrowser.currentPage) {
          $(this).addClass("current-page");
        } else {
          $(this).removeClass("current-page");
        }
      });
      for (
        let i = $(".collection-browser-pagination .page-button").length + 1;
        i <= collectionBrowser.totalPages;
        i++
      ) {
        // --- MODIFIED: Removed inline onclick attribute ---
        $(".collection-browser-pagination").append(
          '<a class="page-button' +
            (i == collectionBrowser.currentPage ? " current-page" : "") +
            '"  data-action="viewPage" data-page-number="' +
            i +
            '">' +
            i +
            "</a>"
        );
      }
      if (collectionBrowser.totalPages > 1) {
        $(".collection-browser-pagination").addClass("shown");
      }
    }

    $("#atheneum-home-page .atheneum-item-pagination-shown").each(function () {
      let infoIconElement = this.querySelector(
        ".atheneum-item .asset-style .info-btn img"
      );
      if (infoIconElement) {
        if (
          SalesToolkit.Theme.getBooleanProperty(
            "hideAssetInformationButton",
            false
          )
        )
          infoIconElement.style.display = "none";
        else {
          _parent.applyImageToElement(
            infoIconElement,
            "assetInformationIcon",
            "img/default/info_icon.svg"
          );
          infoIconElement.style.display = "block";
        }
      }

      let thumbnailElement = this.querySelector(
        '.atheneum-item[data-atheneum-item-type="collection"] .collection-style .thumbnail-image, .atheneum-item[data-atheneum-item-type="asset"] .asset-style .thumbnail'
      );
      if (thumbnailElement) {
        let bgImageURL = thumbnailElement.getAttribute(
          "data-background-image-url"
        );
        if (
          bgImageURL &&
          !thumbnailElement.classList.contains("data-background-update-queued")
        ) {
          thumbnailElement.classList.add("data-background-update-queued");
          _parent.addUrlToRewriteQueue(bgImageURL, function (rewrittenURL) {
            if (rewrittenURL != null && rewrittenURL.href)
              rewrittenURL = rewrittenURL.href;

            if (
              rewrittenURL !== null &&
              rewrittenURL.includes("https:") &&
              !rewrittenURL.includes(
                "keepRatio=true&shouldCrop=false&width=800&height=450"
              )
            ) {
              rewrittenURL =
                rewrittenURL +
                "?keepRatio=true&shouldCrop=false&width=800&height=450";
            }
            thumbnailElement.style.backgroundImage =
              "url('" + rewrittenURL + "')";
            thumbnailElement.style.opacity = 1;
          });
        }
      }
    });

    setTimeout(function () {
      if (
        collectionBrowser.totalPages <= 1 &&
        !collectionBrowser.searchTerm &&
        !collectionBrowser.filtered
      ) {
        $(".collection-browser-pagination").removeClass("shown");
      }
    }, 300);
  }
  //#endregion

  //#region Search Management
  function _setSearchFromLocalStorage() {
    const searchParam = localStorage.searchTerm;
    if (!searchParam) return;

    if (collectionBrowser) {
      collectionBrowser.setPath([]);
    }

    if (searchBox) {
      searchBox.value = searchParam;
    }

    _showSearchBar(true);
    _executeSearchAfterShortDelay(); // Replaces the call to onSearchBoxContentChange for consistency
  }

  function _isSearchBarVisible() {
    return document.body.classList.contains("search-bar-visible");
  }

  function _showSearchNotPermittedWhileSyncingNotification() {
    let message = SalesToolkit.Translation.get(
      "noSearchWhileSyncingMessage",
      "Search functionality is disabled while sync is in progress."
    );
    let title = SalesToolkit.Translation.get(
      "noSearchWhileSyncingTitle",
      "Warning!"
    );
    SalesToolkit.UI.Notifications.showCustomNotification(
      "error",
      message,
      title
    );
  }

  /**
   * Creates and appends a single autocomplete suggestion item to the list.
   * @private
   */
  function _addSearchTag(a, b, inp, arr, i, val) {
    /*check if the item starts with the same letters as the text field value:*/
    if (
      !val ||
      arr[i].substr(0, val.length).toUpperCase() == val.toUpperCase()
    ) {
      /*create a DIV element for each matching element:*/
      b = document.createElement("DIV");
      // Uses the module-scoped `SalesToolkit` variable
      let searchTagLabel = SalesToolkit.Translation.get(
        "searchTagLabel_" + arr[i],
        arr[i]
      );

      if (val) {
        /*make the matching letters bold:*/
        b.innerHTML =
          "<strong>" + searchTagLabel.substr(0, val.length) + "</strong>";
        b.innerHTML += searchTagLabel.substr(val.length);
        b.innerHTML +=
          ": " +
          SalesToolkit.Theme.getStringProperty("searchTagInfo_" + arr[i], "");
      } else {
        b.innerHTML = "<strong>" + searchTagLabel + "</strong>";
        b.innerHTML +=
          ": " +
          SalesToolkit.Theme.getStringProperty("searchTagInfo_" + arr[i], "");
      }

      /*insert a input field that will hold the current array item's value:*/
      b.innerHTML += "<input type='hidden' value='" + arr[i] + ":'>";
      /*execute a function when someone clicks on the item value (DIV element):*/
      b.addEventListener("click", function (e) {
        /*insert the value for the autocomplete text field:*/
        let lastInput = inp.value;

        let searchTagValue = this.getElementsByTagName("input")[0].value;
        let leftover = "";
        if (searchTagValue.endsWith(":")) {
          searchTagValue = searchTagValue.substring(
            0,
            searchTagValue.length - 1
          );
          leftover = ":";
        } else if (searchTagValue.endsWith(": ")) {
          searchTagValue = searchTagValue.substring(
            0,
            searchTagValue.length - 2
          );
          leftover = ": ";
        }
        searchTagValue = SalesToolkit.Translation.get(
          "searchTagLabel_" + searchTagValue,
          searchTagValue
        );
        searchTagValue += leftover;

        if (lastInput === "") inp.value = searchTagValue;
        else if (lastInput.endsWith(" ")) {
          inp.value += " " + searchTagValue;
        } else {
          let lastSpaceIndex = lastInput.lastIndexOf(" ");
          if (lastSpaceIndex < 0) {
            //No spaces so this is the first item, just override
            inp.value = searchTagValue;
          } else {
            lastInput = lastInput.substring(0, lastSpaceIndex);
            inp.value = lastInput + " " + searchTagValue;
          }
        }
        /*close the list of autocompleted values,
            (or any other open lists of autocompleted values:*/
        _closeAllLists(); // Calls the local version
      });
      a.appendChild(b);
    }
  }

  function _closeAllLists(elmnt) {
    const lists = document.getElementsByClassName("autocomplete-items");
    Array.from(lists).forEach((list) => {
      if (elmnt !== list && elmnt !== searchBox) {
        list.parentNode.removeChild(list);
      }
    });
  }

  /**
   * Displays the full list of available search tags/terms when the search bar is empty.
   * @private
   */
  function _showAllSearchTags() {
    if (!SalesToolkit.Theme.getBooleanProperty("allowSearchTerms", true)) {
      return false;
    }

    // Uses the module-scoped `searchBox` variable
    let arr = SalesToolkit.Filters.getSearchTerms();
    let a, b, i;

    // Calls the local `_closeAllLists` and uses local `keepSearchTagsOpen`
    _closeAllLists();
    keepSearchTagsOpen = true;

    a = document.createElement("DIV");
    a.setAttribute("id", searchBox.id + "autocomplete-list");
    a.setAttribute("class", "autocomplete-items");
    searchBox.parentNode.appendChild(a);

    let basicTerms = SalesToolkit.Theme.getStringProperty(
      "basicSearchTerms",
      "description,keywords,mimetype,name,views"
    );

    for (i = 0; i < arr.length; i++) {
      if (basicTerms.indexOf(arr[i]) < 0) {
        if (
          SalesToolkit.Theme.getBooleanProperty(
            "hideEmptyTagsFromSearchTerms",
            true
          )
        ) {
          let tag = SalesToolkit.Admin.tags.filter((x) => x.name === arr[i]);
          if (tag.length > 0) {
            tag = tag[0];
            let options = tag.options.filter(
              (x) =>
                (x.hasOwnProperty("assetIds") && x.assetIds.length > 0) ||
                (x.hasOwnProperty("collectionIds") &&
                  x.collectionIds.length > 0)
            );
            options = options.filter(
              (x) => x.assetIds in SalesToolkit.Library.assets
            );
            options = options.filter(
              (x) => x.collectionIds in SalesToolkit.Library.collections
            );

            if (options.length == 0) continue;
          }
        }
      }
      // Calls the local `_addSearchTag`
      _addSearchTag(a, b, searchBox, arr, i);
    }
  }

  /**
   * Initializes the autocomplete functionality on the search input field.
   * @private
   * @param {HTMLInputElement} inp The input element for the search box.
   * @param {string[]} arr An array of possible autocomplete terms.
   */
  function _autocomplete(inp, arr) {
    let currentFocus;
    /*execute a function when someone writes in the text field:*/
    inp.addEventListener("input", function (e) {
      let a,
        b,
        i,
        val = this.value;
      /*close any already open lists of autocompleted values*/
      _closeAllLists(); // Calls the local version
      if (!val) {
        return false;
      }

      if (val.indexOf(" ") >= 0) {
        val = val.substring(val.lastIndexOf(" ") + 1);
      }
      currentFocus = -1;
      /*create a DIV element that will contain the items (values):*/
      a = document.createElement("DIV");
      a.setAttribute("id", this.id + "autocomplete-list");
      a.setAttribute("class", "autocomplete-items");
      /*append the DIV element as a child of the autocomplete container:*/
      this.parentNode.appendChild(a);
      /*for each item in the array...*/
      let basicTerms = SalesToolkit.Theme.getStringProperty(
        "basicSearchTerms",
        "description,keywords,mimetype,name,views"
      );

      for (i = 0; i < arr.length; i++) {
        if (basicTerms.indexOf(arr[i]) < 0) {
          if (
            SalesToolkit.Theme.getBooleanProperty(
              "hideEmptyTagsFromSearchTerms",
              true
            )
          ) {
            let tag = SalesToolkit.Admin.tags.filter((x) => x.name === arr[i]);
            if (tag.length > 0) {
              tag = tag[0];
              let options = tag.options.filter(
                (x) =>
                  (x.hasOwnProperty("assetIds") && x.assetIds.length > 0) ||
                  (x.hasOwnProperty("collectionIds") &&
                    x.collectionIds.length > 0)
              );
              //Make sure the assets exist
              options = options.filter(
                (x) => x.assetIds in SalesToolkit.Library.assets
              );
              options = options.filter(
                (x) => x.collectionIds in SalesToolkit.Library.collections
              );
              if (options.length == 0) continue;
            }
          }
        }
        // Calls the local version of _addSearchTag
        _addSearchTag(a, b, inp, arr, i, val);
      }
    });

    /*execute a function presses a key on the keyboard:*/
    inp.addEventListener("keydown", function (e) {
      let x = document.getElementById(this.id + "autocomplete-list");
      if (x) x = x.getElementsByTagName("div");
      if (e.keyCode == 40) {
        /*If the arrow DOWN key is pressed,
            increase the currentFocus variable:*/
        currentFocus++;
        /*and and make the current item more visible:*/
        addActive(x);
      } else if (e.keyCode == 38) {
        //up
        /*If the arrow UP key is pressed,
            decrease the currentFocus variable:*/
        currentFocus--;
        /*and and make the current item more visible:*/
        addActive(x);
      } else if (e.keyCode == 13) {
        /*If the ENTER key is pressed, prevent the form from being submitted,*/
        e.preventDefault();
        if (currentFocus > -1) {
          /*and simulate a click on the "active" item:*/
          if (x) x[currentFocus].click();
        }
      }
    });

    function addActive(x) {
      /*a function to classify an item as "active":*/
      if (!x) return false;
      /*start by removing the "active" class on all items:*/
      removeActive(x);
      if (currentFocus >= x.length) currentFocus = 0;
      if (currentFocus < 0) currentFocus = x.length - 1;
      /*add class "autocomplete-active":*/
      x[currentFocus].classList.add("autocomplete-active");
    }

    function removeActive(x) {
      /*a function to remove the "active" class from all autocomplete items:*/
      for (let i = 0; i < x.length; i++) {
        x[i].classList.remove("autocomplete-active");
      }
    }
  }

  function _hideSearchBar() {
    document.body.classList.remove("search-bar-visible");
    if (document.getElementById("searchbox-line")) {
      document.getElementById("searchbox-line").style.width = "0px";
    }
    if (searchBox) {
      searchBox.blur();
    }
    _closeAllLists();
  }

  function _showSearchBar(ignoreBlock = false) {
    if (
      !ignoreBlock &&
      SalesToolkit.Theme.getBooleanProperty(
        "preventSearchWhenSyncInProgress",
        true
      ) &&
      librarySyncInProgress
    ) {
      searchBox.value = "";
      _hideSearchBar();
      _showSearchNotPermittedWhileSyncingNotification();
      return;
    }

    document.body.classList.add("search-bar-visible");
    document.getElementById("searchbox-line").style.width = "calc(100% + 28px)";

    $("input#search").on("input", function () {
      if (searchBox.value !== "") {
        document.getElementById("sortby-item").style.visibility = "visible";
      } else {
        document.getElementById("sortby-item").style.visibility = "hidden";
      }
    });
  }

  function _showTreeViewIfPreviouslyVisible() {
    if (treeViewHiddenWhenSearchBoxOpened) {
      _toggleTree();
    }
    treeViewHiddenWhenSearchBoxOpened = undefined;
  }

  function _clearSearch() {
    if (searchBox) {
      searchBox.value = "";
    }
    $("#topLevelFilters").css("display", "flex");
    if (
      SalesToolkit.Theme.getBooleanProperty("useCalview", false) &&
      localStorage.calViewActive == "true"
    ) {
      $("body").addClass("showing-cal-view");
    } else {
      $("body").removeClass("showing-cal-view");
    }
    delete localStorage.searchTerm;
    if (loadedHeaderId && loadedHeaderId.length > 0)
      $("#collectionHeader").show();
    if (collectionBrowser) {
      if (collectionBrowser.searchTerm) {
        collectionBrowser.clearSearch();
        document
          .querySelector(".collection-browser-pagination")
          .classList.remove("shown");
      }
    }
    if (collectionBrowser?.path?.length == 0) {
      document.querySelector("#breadcrumbsbuttonhide").style.visibility =
        "hidden";
    }
  }

  function _clearSearchAndHideSearchBar() {
    _clearSearch();
    _hideSearchBar();
    _showTreeViewIfPreviouslyVisible();
  }

  function _executeSearch(searchTerm) {
    if (collectionBrowser) {
      if (searchTerm.length >= 3) {
        _parent.trackAppSearch(searchTerm);
        document.querySelector("#breadcrumbsbuttonhide").style.visibility =
          "visible";
        collectionBrowser.search(searchTerm);
        if (collectionBrowser.totalPages > 1) {
          document
            .querySelector(".collection-browser-pagination")
            .classList.add("shown");
        } else {
          document
            .querySelector(".collection-browser-pagination")
            .classList.remove("shown");
        }
        localStorage.searchTerm = searchTerm;
        $("#topLevelFilters").hide();
        $("body").removeClass("showing-cal-view");
        $("body").addClass("searching");
      } else {
        if (loadedHeaderId && loadedHeaderId.length > 0)
          $("#collectionHeader").show();
        if (collectionBrowser) {
          collectionBrowser.clearSearch();
          if (typeof _updateURLsAndPageButtonLinks === "function") {
            _updateURLsAndPageButtonLinks();
          }
        }
        if (searchTerm.length == 0) {
          if (document.body.classList.contains("search-bar-visible"))
            _showAllSearchTags();
        }
        if (collectionBrowser.path.length == 0) {
          document.querySelector("#breadcrumbsbuttonhide").style.visibility =
            "hidden";
        }
        $("body").removeClass("searching");
        delete localStorage.searchTerm;
        $("#topLevelFilters").css("display", "flex");
        if (
          SalesToolkit.Theme.getBooleanProperty("useCalview", false) &&
          localStorage.calViewActive == "true"
        ) {
          $("body").addClass("showing-cal-view");
        } else {
          $("body").removeClass("showing-cal-view");
        }
      }
    }
  }

  function _executeSearchAfterShortDelay() {
    if (executeSearchTimeout) {
      clearTimeout(executeSearchTimeout);
    }
    if (collectionBrowser && collectionBrowser._searchTimeout) {
      clearTimeout(collectionBrowser._searchTimeout);
      collectionBrowser._searchTimeout = null;
    }
    executeSearchTimeout = setTimeout(function () {
      if (searchBox) {
        _executeSearch(searchBox.value);
      }
    }, executeSearchDelayInMilliseconds);
  }

  function _onHomePageBackButtonClick() {
    if (
      $("body").hasClass("filtering") &&
      SalesToolkit.Theme.getBooleanProperty("clearFiltersOnBackButton", true)
    ) {
      _clearAllFilters();
    }

    if ($("body").hasClass("showing-cal-view")) {
      _calViewGoBack();
      return;
    }
    _parent.trackAppClick(
      $("#breadcrumbsbuttonhide"),
      "#breadcrumbsbuttonhide",
      "trackingBackButton",
      SalesToolkit.Translation.get("trackingBackButton", "trackingBackButton")
    );

    if (pluginBackEnabled) {
      pluginBackEnabled = false;
      _parent.showPluginIframe();
      if (SalesToolkit.Plugins.activePluginId)
        _parent.loadPlugin(SalesToolkit.Plugins.activePluginId);
    }

    if (searchBox && searchBox.value != "") {
      _clearSearchAndHideSearchBar();
      return;
    }

    let refresh = false;
    if (collectionBrowser.currentPage > 1) refresh = true;

    if (collectionBrowser.path.length <= 1) {
      collectionBrowser.setPath([]);
      _calViewGoBack();
      collectionBrowser.viewPage(1);
    } else {
      let breadcrumbs = $(".breadsmall");
      if (breadcrumbs.length < 2) {
        if (breadcrumbs.length > 0) breadcrumbs[0].click();
      } else {
        breadcrumbs[breadcrumbs.length - 2].click();
      }
      collectionBrowser.viewPage(1);
    }

    if (refresh) collectionBrowser.refresh();
  }

  function _toggleSearchBarVisibility() {
    if (
      SalesToolkit.Theme.getBooleanProperty(
        "preventSearchWhenSyncInProgress",
        true
      ) &&
      librarySyncInProgress
    ) {
      if (searchBox) searchBox.value = "";
      _hideSearchBar();
      _showSearchNotPermittedWhileSyncingNotification();
      return;
    }

    _parent.trackAppClick(
      $(".searchdropdown"),
      ".searchdropdown",
      "trackingSearchToggle",
      SalesToolkit.Translation.get(
        "trackingSearchToggle",
        "trackingSearchToggle"
      )
    );

    if (!_isSearchBarVisible()) {
      _showSearchBar();
      if (SalesToolkit.Theme.getBooleanProperty("allowSearchTerms", true)) {
        _showAllSearchTags();
      }
      _parent.trackAppClick(
        $(".searchdropdown"),
        ".searchdropdown",
        "trackingSearchToggleShow",
        SalesToolkit.Translation.get(
          "trackingSearchToggleShow",
          "trackingSearchToggleShow"
        )
      );
      if (searchBox) searchBox.focus();

      if (localStorage.hideTree != "true") {
        treeViewHiddenWhenSearchBoxOpened = true;
        _toggleTree();
      }
    } else {
      _parent.trackAppClick(
        $(".searchdropdown"),
        ".searchdropdown",
        "trackingSearchToggleHide",
        SalesToolkit.Translation.get(
          "trackingSearchToggleHide",
          "trackingSearchToggleHide"
        )
      );
      _hideSearchBar();
      _closeAllLists();
      _showTreeViewIfPreviouslyVisible();
    }
  }
  //#endregion

  //#region Sync Management
  function _handleSyncPressStart() {
    // Set timeout to show the sync report after 10 seconds.
    pressTimer = setTimeout(function () {
      if (
        SalesToolkit.CrossPlatform &&
        typeof SalesToolkit.CrossPlatform.showSyncReport === "function"
      ) {
        SalesToolkit.CrossPlatform.showSyncReport();
      }
    }, 10000);
  }

  function _handleSyncPressEnd() {
    // Clear the timer if the press is released before 10 seconds.
    if (pressTimer) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }
  }

  //#endregion

  //#region Plugins & Journals

  /**
   * Finds the top-most parent collection for a given collection ID.
   * @param {string} collectionId - The ID of the starting collection.
   * @returns {object|undefined} The top-level collection object.
   */
  function _getTopLevelCollectionForCollectionId(collectionId) {
    let collection = SalesToolkit.Library.collections[collectionId];
    while (collection?.containedBy) {
      collection = SalesToolkit.Library.collections[collection.containedBy];
    }
    return collection;
  }

  /**
   * Activates a top-level filter, updating the view and UI.
   * @param {string} filterName - The name of the filter to activate.
   * @param {boolean} [skipCB=false] - If true, skips refreshing the collection browser.
   */
  function _activateFilter(filterName, skipCB = false) {
    if (filterName === activeFilter) {
      return;
    }

    const aopFilterName = SalesToolkit.Translation.get("aopFilterName", "AOP");
    let shouldCalviewGoBack = false;

    if (activeFilter === aopFilterName) {
      _clearAllFilters(false); // Assumes global function
      _clearSearch();
      if (collectionBrowser) {
        collectionBrowser.setPath([]);
      }
      shouldCalviewGoBack = true;
    }

    if (filterName === aopFilterName) {
      const backButton = document.getElementById("breadcrumbsbuttonhide");
      if (backButton) {
        backButton.style.visibility = "visible";
      }
    }

    _clearArticleHeader(); // Assumes global function

    activeFilter = filterName;

    if (shouldCalviewGoBack) {
      _calViewGoBack();
    }

    if (typeof collectionBrowser !== "undefined") {
      collectionBrowser.activeTopLevelFilter = filterName;
      if (!skipCB) collectionBrowser.refresh();
      try {
        if (typeof treeView !== "undefined" && treeView !== null) {
          treeView.mirror(collectionBrowser);
          if (!skipCB) {
            treeView.refresh();
            repopulateWhenReady();
          }
        }
      } catch (err) {
        console.error("Error updating treeView:", err);
      }
    }

    _parent.trackAppClick(
      $("#topLevelFilter" + filterName),
      "#topLevelFilter" + filterName,
      "trackingTopLevelFilterActivate",
      SalesToolkit.Translation.get(
        "trackingTopLevelFilterActivate",
        "Top Level Filter Activated"
      )
    );

    $(".topLevelFilter").removeClass("active");
    $("#topLevelFilter" + filterName).addClass("active");

    try {
      const calViewUI = document.getElementById("atheneum-calview-ui");
      if (calViewUI && calViewUI.contentWindow) {
        calViewUI.contentWindow.loadYear(activeFilter);
      }
    } catch (err) {
      console.error("Error loading year in calendar view:", err);
    }
  }

  function _renderCollectionBrowserPlugins() {
    if (
      SalesToolkit.Theme.getBooleanProperty(
        "salesToolkitPluginsAtTopLevelOnly",
        true
      ) &&
      collectionBrowser &&
      collectionBrowser.path.length != 0
    ) {
      return;
    }

    if (
      SalesToolkit.UI.CollectionBrowser.Plugins &&
      SalesToolkit.UI.CollectionBrowser.Plugins.length > 0
    ) {
      let $lastElement = null;
      for (
        let i = 0;
        i < SalesToolkit.UI.CollectionBrowser.Plugins.length;
        i++
      ) {
        const pluginHtml = SalesToolkit.UI.CollectionBrowser.Plugins[i].html;
        const plugin = SalesToolkit.UI.CollectionBrowser.Plugins[i].plugin;

        if (
          collectionBrowser &&
          collectionBrowser.searchTerm &&
          collectionBrowser.searchTerm.length > 0
        ) {
          break;
        }

        if (plugin.allowedCollections) {
          let allowedCol = plugin.allowedCollections;
          if (typeof allowedCol === "string")
            allowedCol = JSON.parse(allowedCol);
          if (allowedCol) {
            if (
              allowedCol.length > 0 &&
              collectionBrowser &&
              collectionBrowser.path.length == 0
            )
              continue;

            let currentColId =
              collectionBrowser.path[collectionBrowser.path.length - 1];
            if (
              allowedCol.length > 0 &&
              plugin.allowedCollections.indexOf(currentColId) < 0
            )
              continue;
          }
        }

        if (
          $("#libraryLaunch" + SalesToolkit.UI.CollectionBrowser.Plugins[i].id)
            .length == 0
        ) {
          if (
            SalesToolkit.Theme.getBooleanProperty(
              "salesToolkitPluginsBeforeCollections",
              true
            )
          ) {
            if ($lastElement == null) {
              $(collectionBrowser.element).prepend(pluginHtml);
              $lastElement = $(
                "#libraryLaunch" +
                  SalesToolkit.UI.CollectionBrowser.Plugins[i].id
              );
            } else {
              $lastElement.after(pluginHtml);
              $lastElement = $(
                "#libraryLaunch" +
                  SalesToolkit.UI.CollectionBrowser.Plugins[i].id
              );
            }
          } else {
            $(collectionBrowser.element).append(pluginHtml);
          }
        }
      }
    }
  }

  function _hideEmptyTopLevelFilters() {
    if (typeof collectionBrowser === "undefined") return;
    let topLevelFilters = SalesToolkit.Theme.getStringProperty(
      "topLevelNameFilters",
      ""
    ).split(",");
    let backupFilterToActivate = null;
    let noActiveFilter = false;
    const aopFilterName = SalesToolkit.Translation.get("aopFilterName", "AOP");

    for (let i = 0; i < topLevelFilters.length; i++) {
      const filterName = topLevelFilters[i].trim();
      const filter = $("#topLevelFilters").find("#topLevelFilter" + filterName);
      let collections = [];

      if (collectionBrowser.topLevelSectioned) {
        collections = SalesToolkit.Library.getTopLevelCollections();
        let collection =
          SalesToolkit.Library.collections[
            collectionBrowser.topLevelSectionId
          ] || collections[0];
        collectionBrowser.topLevelSectionId = collection.id;
        collections = collection.stories;
        if (filterName === aopFilterName) {
          const aopCollectionName = SalesToolkit.Theme.getStringProperty(
            "aopCollectionName",
            "Ahead of Print"
          );
          collections = SalesToolkit.Library.collections[
            collectionBrowser.topLevelSectionId
          ].stories.filter((x) => x.name == aopCollectionName);
        } else {
          collections = collections.filter(
            (x) => x.name.toLowerCase().indexOf(filterName.toLowerCase()) >= 0
          );
        }
      } else if (collectionBrowser.topLevelCollection) {
        let collection =
          SalesToolkit.Library.collections[
            collectionBrowser.topLevelCollection
          ];
        if (collection && SalesToolkit.User.canSee(collection)) {
          collections = collection.stories;
          if (filterName === aopFilterName) {
            const aopCollectionName = SalesToolkit.Theme.getStringProperty(
              "aopCollectionName",
              "Ahead of Print"
            );
            collections = SalesToolkit.Library.collections[
              collectionBrowser.topLevelSectionId
            ].stories.filter((x) => x.name == aopCollectionName);
          } else {
            collections = collections.filter(
              (x) => x.name.toLowerCase().indexOf(filterName.toLowerCase()) >= 0
            );
          }
        }
      } else {
        collections = SalesToolkit.Library.getTopLevelCollections();
        if (filterName === aopFilterName) {
          const aopCollectionName = SalesToolkit.Theme.getStringProperty(
            "aopCollectionName",
            "Ahead of Print"
          );
          collections = SalesToolkit.Library.collections[
            collectionBrowser.topLevelSectionId
          ].stories.filter((x) => x.name == aopCollectionName);
        } else {
          collections = collections.filter(
            (x) => x.name.toLowerCase().indexOf(filterName.toLowerCase()) >= 0
          );
        }
      }

      filter.show();
      if (collections.length == 0) {
        filter.hide();
        if (filter.hasClass("active") && backupFilterToActivate != null)
          _activateJournalWithId(backupFilterToActivate);
        else if (filter.hasClass("active")) noActiveFilter = true;
      } else if (backupFilterToActivate == null) {
        backupFilterToActivate = filterName;
        if (noActiveFilter) _activateJournalWithId(backupFilterToActivate);
      }
    }
  }

  function _activateJournalWithId(id, forceUpdateTabs = false) {
    const element = $("#journalTab_" + id).get(0);
    if (!element) return;

    if (
      !forceUpdateTabs &&
      collectionBrowser.topLevelSectionId === id &&
      element.classList.contains("active")
    )
      return;

    $(".journalTab.active").removeClass("active");
    $(element).toggleClass("active", true);
    collectionBrowser.topLevelSectionId = id;
    collectionBrowser.refresh();
    if (typeof treeView !== "undefined" && treeView !== null) {
      treeView.mirror(collectionBrowser);
      treeView.refresh();
      repopulateWhenReady();
    }
    element.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "center",
    });

    try {
      $("#atheneum-calview-ui")[0].contentWindow.setJournalId(id);
      loadedHeaderId = "";
    } catch (err) {
      console.log(err);
    }

    _hideEmptyTopLevelFilters();
  }
  //#endregion

  //#region News Ticker
  /**
   * Sets up and initializes the news ticker component.
   */
  function _setupNewsTicker() {
    const salestoolkitNewsTickerOptions = {
      speed: 0.4,
      pauseOnItems: 2500,
    };

    _updateTickerCallback = function () {
      const latestNewsItems = SalesToolkit.NewsFeed.getLatest(10);
      const $wrapper = $("#salestoolkit-news-ticker-wrapper").empty();
      let html = '<ul id="js-news" class="js-hidden">';

      if (latestNewsItems.length > 0) {
        latestNewsItems.forEach((item) => {
          const thumbnailUrl =
            SalesToolkit.CrossPlatform.GetFeedItemThumbnail(item) || "";
          const thumbnail = thumbnailUrl
            ? `<div style="display: inline-block; width: 35px; text-align: center;"><img style="max-width: 35px; max-height: 20px; margin-bottom: 3px;" src="${thumbnailUrl}"></div>`
            : "";
          html += `<li class="news-item">${thumbnail}<a href="page_news_item.html?newsItemId=${item.id}"> ${item.name}</a></li>`;
        });
      } else {
        html += '<li class="news-item">Loading News Feed...</li>';
      }

      html += "</ul>";
      $wrapper.html(html);
      $("#js-news").ticker(salestoolkitNewsTickerOptions); // Assumes .ticker() is a jQuery plugin
    };

    _updateTickerCallback();
  }
  //#endregion

  //#region Item & URL Management
  function _updateItemCartAndFavouritesTagsAndReturnFalse(item) {
    _updateItemCartAndFavouritesTags(item);
    return false;
  }

  function _getUrlVars() {
    const vars = {};
    window.location.href.replace(
      /[?&]+([^=&]+)=([^&]*)/gi,
      function (m, key, value) {
        vars[key] = value;
      }
    );
    return vars;
  }

  function _getUrlParam(parameter, defaultvalue) {
    let urlparameter = defaultvalue;
    if (window.location.href.includes(parameter)) {
      urlparameter = _getUrlVars()[parameter];
    }
    return urlparameter;
  }

  function _setupSearchFromParam() {
    let searchParam = _getUrlParam("search", "");
    if (searchParam.length === 0) return;

    searchParam = unescape(searchParam);

    if (collectionBrowser) {
      collectionBrowser.setPath([]);
    }

    if (searchBox) searchBox.value = searchParam;

    // These functions are defined in the module and will be available
    _showSearchBar(true);
    _executeSearchAfterShortDelay();
  }

  function _initializeIconVariables() {
    FAVOURITES_HEART_FULL = SalesToolkit.Theme.getStringProperty(
      "salestoolkitAssetTileIconFavouritesFilled",
      "img/default/favourites_full_icon.svg"
    );
    FAVOURITES_HEART_EMPTY = SalesToolkit.Theme.getStringProperty(
      "salestoolkitAssetTileIconFavourites",
      "img/default/favourites_empty_icon_sml.svg"
    );
    SHOPPING_CART_FULL = SalesToolkit.Theme.getStringProperty(
      "salestoolkitAssetTileIconCartFilled",
      "img/default/shopping_cart_full_new.svg"
    );
    SHOPPING_CART_EMPTY = SalesToolkit.Theme.getStringProperty(
      "salestoolkitAssetTileIconCart",
      "img/default/shopping_cart_new_blue.svg"
    );

    if (SalesToolkit.CrossPlatform.enabled) {
      // Helper function to handle the rewrite and update the specific local variable
      const processIcon = function (originalUrl, updateVariableCallback) {
        // Check if already cached in the Rewrite Cache
        if (
          SalesToolkit.CrossPlatform.urlRewriteCache.hasOwnProperty(originalUrl)
        ) {
          updateVariableCallback(
            SalesToolkit.CrossPlatform.urlRewriteCache[originalUrl]
          );
        } else {
          // Queue it for rewriting
          _parent.addUrlToRewriteQueue(originalUrl, function (rewrittenURL) {
            // A. Update the local variable via the callback
            updateVariableCallback(rewrittenURL);

            // B. Visually update any images in the DOM that currently use the old HTTP URL
            // This catches icons that rendered before the rewrite finished
            $('img[src="' + originalUrl + '"]').attr("src", rewrittenURL);
          });
        }
      };

      // Explicitly update the module-level variables
      processIcon(FAVOURITES_HEART_FULL, function (val) {
        FAVOURITES_HEART_FULL = val;
      });
      processIcon(FAVOURITES_HEART_EMPTY, function (val) {
        FAVOURITES_HEART_EMPTY = val;
      });
      processIcon(SHOPPING_CART_FULL, function (val) {
        SHOPPING_CART_FULL = val;
      });
      processIcon(SHOPPING_CART_EMPTY, function (val) {
        SHOPPING_CART_EMPTY = val;
      });
    }
  }

  function _updateItemCartAndFavouritesTags(item) {
    const itemElement = document.querySelector(
      '.atheneum-item[data-atheneum-item-id="' + item.id + '"]'
    );
    if (!itemElement) return false;

    _initializeIconVariables(); // Ensure icons are up-to-date

    const favouritesButton = itemElement.querySelector(
      '.atheneum-item-action[data-atheneum-item-action="toggle-favourite"]'
    );
    const cartButton = itemElement.querySelector(
      '.atheneum-item-action[data-atheneum-item-action="toggle-cart"]'
    );

    $("body > .tooltip").remove();

    let itemInFavourites = false;
    let itemInCart = false;
    if (item.type === "collection") {
      itemInFavourites = SalesToolkit.Favourites.containsCollection(item.id);
      itemInCart = SalesToolkit.Cart.containsCollection(item.id);
    } else {
      // Assume asset
      itemInFavourites = SalesToolkit.Favourites.containsAsset(item.id);
      itemInCart = SalesToolkit.Cart.containsAsset(item.id);
    }

    if (favouritesButton) {
      const img = favouritesButton.querySelector("img");
      if (itemInFavourites) {
        if (img) img.src = FAVOURITES_HEART_FULL;
        favouritesButton.classList.add("already-clicked");
        favouritesButton.setAttribute(
          "data-original-title",
          SalesToolkit.Translation.get(
            "favouritesToggleRemove",
            "Remove from Favourites"
          )
        );
      } else {
        if (img) img.src = FAVOURITES_HEART_EMPTY;
        favouritesButton.classList.remove("already-clicked");
        favouritesButton.setAttribute(
          "data-original-title",
          SalesToolkit.Translation.get(
            "favouritesToggleAdd",
            "Add to Favourites"
          )
        );
      }
    }

    if (cartButton) {
      const img = cartButton.querySelector("img");
      if (itemInCart) {
        if (img) img.src = SHOPPING_CART_FULL;
        cartButton.classList.add("already-clicked");
        cartButton.setAttribute(
          "data-original-title",
          SalesToolkit.Translation.get("cartToggleRemove", "Remove from Cart")
        );
      } else {
        if (img) img.src = SHOPPING_CART_EMPTY;
        cartButton.classList.remove("already-clicked");
        cartButton.setAttribute(
          "data-original-title",
          SalesToolkit.Translation.get("cartToggleAdd", "Add to Cart")
        );
      }
    }
    SalesToolkit.UI.Cart.updateMenuCartBadge();
  }

  function _breadcrumbClick(elem, isHome) {
    const key = "trackingBreadcrumb";
    const description = SalesToolkit.Translation.get(key, key);
    if (isHome) {
      _parent.trackAppClick(
        $(elem),
        "#salestoolkit-breadcrumbs li:first-child",
        key,
        description
      );
    } else {
      _parent.trackAppClick(
        $(elem),
        `a.breadsmall[data-collection-id="${$(elem).data("collection-id")}"]`,
        key,
        description
      );
    }
  }

  function _showCurrentCollectionMeetingScreen() {
    let currentCollectionId =
      collectionBrowser?.path?.length > 0
        ? collectionBrowser.path[collectionBrowser.path.length - 1]
        : undefined;
    const meetingCollections =
      SalesToolkit.Forms.getCollectionsWithMeetingForms();
    let collection = currentCollectionId
      ? SalesToolkit.Library.collections[currentCollectionId]
      : null;

    while (collection && !meetingCollections.includes(currentCollectionId)) {
      currentCollectionId = collection.containedBy;
      collection = currentCollectionId
        ? SalesToolkit.Library.collections[currentCollectionId]
        : null;
    }

    _parent.showMeetingsScreen(currentCollectionId);
  }
  //#endregion

  //#region Calendar View Management
  function _resizeCalView(isFullScreen = false) {
    if (!document.body.classList.contains("module-index")) return;
    let mainDivBottom =
      $("#main-div-center").offset().top + $("#main-div-center").outerHeight();
    let calview = $("#atheneum-calview-ui");
    let height = mainDivBottom - calview.offset().top;
    if (calview.offset().top + height > window.innerHeight) {
      height = window.innerHeight - calview.offset().top - 15;
    }

    if (isFullScreen) {
      height += $(".page-footer").height() - 5;
    } else {
      height -= 6;
    }

    if (SalesToolkit.Theme.getBooleanProperty("scrollTreeView", false)) {
      $("#atheneum-tree-view").height(height);
      $("#atheneum-tree-view").css("overflow-y", "scroll");
      $("#atheneum-tree-view")
        .parents(".portlet.light")
        .css("padding-bottom", 0);
      $("#atheneum-tree-view")
        .parents(".portlet.light")
        .css("margin-bottom", 0);
    }

    calview.height(height);
  }
  function _setupCalViewBridge() {
    _calViewBridge = {
      // Provide direct access to data needed at load time
      initialJournalId: _getTopLevelSectionId(),
      atheneumInstance: SalesToolkit, // Pass the main instance
      jquery: $, // Pass jQuery

      // Provide methods for actions and data retrieval
      getItemTemplate: _getItemTemplate,
      getTopLevelSectionId: _getTopLevelSectionId,
      loadCollection: function (collectionId) {
        collectionBrowser?.viewCollection(collectionId);
      },
      setPath: function (path, ignoreTracking = false) {
        collectionBrowser?.setPath(path, ignoreTracking);
      },
      getPath: function () {
        if (collectionBrowser) {
          return collectionBrowser.path;
        }
        return []; // Return an empty array as a safe default
      },
      resize: function () {
        _resizeCalView(false);
      },
      activateFilter: function (filterName, skipCB = false) {
        _activateFilter(filterName, skipCB);
      },
    };
  }
  function _setupCalViewTabs(forceUpdateTabs = false) {
    if (!collectionBrowser || !collectionBrowser.topLevelSectioned) {
      $("#page-calview-tabs").toggleClass("hidden", true);
      return;
    }

    $("#page-calview-tabs").empty();
    const journals = SalesToolkit.Library.getTopLevelCollections();
    let activeDone = false;
    let activeId = null;

    if (
      typeof collectionBrowser.topLevelSectionId === "string" &&
      journals.some((x) => x.id === collectionBrowser.topLevelSectionId)
    ) {
      activeId = collectionBrowser.topLevelSectionId;
      activeDone = true;
    }

    for (const journal of journals) {
      if ($("#journalTab_" + journal.id).length > 0) continue;

      $("#page-calview-tabs").append(
        `<div id="journalTab_${journal.id}" data-action="activateJournal" data-journal-id="${journal.id}" class="journalTab"><img src="" style="display:none"/><span>${journal.name}</span></div>`
      );

      const iconElement = $("#journalTab_" + journal.id);
      if (!activeDone) {
        iconElement.addClass("active");
        activeDone = true;
        activeId = journal.id;
      } else if (activeId !== null && journal.id === activeId) {
        iconElement.addClass("active");
      }
      iconElement.prop("title", journal.name);
      const spanElement = $("#journalTab_" + journal.id + " span");

      if (!journal.icon?.url) {
        spanElement.show();
        continue;
      }

      const imgURL = journal.icon.url;
      if (imgURL && !iconElement.hasClass("data-background-update-queued")) {
        iconElement.addClass("data-background-update-queued");
        _parent.addUrlToRewriteQueue(imgURL, function (rewrittenURL) {
          if (rewrittenURL?.href) rewrittenURL = rewrittenURL.href;
          if (
            rewrittenURL?.includes("https:") &&
            !rewrittenURL.includes(
              "keepRatio=true&shouldCrop=false&width=500&height=45"
            )
          ) {
            rewrittenURL +=
              "?keepRatio=true&shouldCrop=false&width=500&height=45";
          }
          iconElement
            .find("img")
            .off("load")
            .on("load", function () {
              iconElement.addClass("image-tab");
              iconElement.find("img").show();
              spanElement.hide();
            })
            .attr("src", rewrittenURL);
        });
      }
    }

    $("#page-calview-tabs").removeClass("hidden").css("display", "flex");

    if (localStorage["currentCalViewJournal"] != undefined) {
      const journalId = localStorage["currentCalViewJournal"];
      if (journals.some((x) => x.id === journalId)) activeId = journalId;
    }

    if (activeId != null) _activateJournalWithId(activeId, forceUpdateTabs);
  }

  function _calViewReloadConditional() {
    // Safely access contentWindow and then call the function if it exists.
    $("#atheneum-calview-ui")[0]?.contentWindow?.loadYearIfInYears?.();
  }

  function _calViewGoBack() {
    const calWindow = $("#atheneum-calview-ui")[0]?.contentWindow;

    // If calWindow exists, attempt to call each function.
    calWindow?.goBack?.();
    calWindow?.loadDefaultYear?.();
  }

  function _calViewLoadCollection(collectionId) {
    const collection = SalesToolkit.Library.collections[collectionId];
    if (!collection || !SalesToolkit.User.canSee(collection)) return;

    const calWindow = $("#atheneum-calview-ui")[0]?.contentWindow;

    // Call loadCollection only if it's available.
    calWindow?.loadCollection?.(collectionId, true);

    if (collection.assets.length > 0) {
      // Call scrollToCollection only if it's available.
      calWindow?.scrollToCollection?.(collectionId);
    }
  }
  //#endregion

  //#region Setup and CollectionBrowser
  function _setupCollectionBrowserAndFilters() {
    SalesToolkit.Pages.CurrentPage.requiresLogin = true;

    let collectionId =
      SalesToolkit.Pages.CurrentPage.getQueryParam("collectionId") || null;

      if(!_viewCollectionId)
    _viewCollectionId =
      SalesToolkit.Pages.CurrentPage.getQueryParam("viewCollection");
    if (
      _viewCollectionId &&
      SalesToolkit.Library.collections[_viewCollectionId]
    ) {
      // Store/Override the collectionId so all subsequent logic (Journal tabs, filters, initial view) uses this
      collectionId = _viewCollectionId;
      _viewCollectionLoaded = true;
    }

    if (collectionId) {
      const journal = _getTopLevelCollectionForCollectionId(collectionId);
      if (journal) {
        const journals = SalesToolkit.Library.getTopLevelCollections();
        if (journals.some((x) => x.id === journal.id)) {
          localStorage["currentCalViewJournal"] = journal.id;
        }
      }

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "clearFiltersOnCollectionLoad",
          true
        ) &&
        typeof clearAllFilters === "function"
      ) {
        _clearAllFilters();
      }
    }

    let topLevelFilters = SalesToolkit.Theme.getStringProperty(
      "topLevelNameFilters",
      ""
    ).split(",");
    const $topLevelFiltersContainer = $("#topLevelFilters").empty();
    let activeFilterName = "";

    topLevelFilters.forEach((filterName, index) => {
      filterName = filterName.trim();
      if (!filterName) return;

      let activeClass = "";
      if (
        localStorage.currentCalViewYear === filterName ||
        (!localStorage.currentCalViewYear && index === 0)
      ) {
        activeFilterName = filterName;
        activeClass = "active";
      }

      const $filterSpan = $("<span></span>")
        .attr("id", `topLevelFilter${filterName}`)
        .attr("data-action", "activateFilter")
        .data("filter-name", filterName)
        .addClass(`topLevelFilter ${activeClass}`)
        .text(filterName);

      $topLevelFiltersContainer.append($filterSpan);
    });

    $topLevelFiltersContainer.css("display", "flex");

    // Initialize the Collection Browser
    collectionBrowser = SalesToolkit.UI.CollectionBrowser(
      document.getElementById("atheneum-home-page"),
      SalesToolkit.getHomeCollectionID(),
      SalesToolkit.Theme.getBooleanProperty("topLevelSectioned", false)
    );
    collectionBrowser.activeTopLevelFilter = activeFilterName;

    collectionBrowser.searchOptions = {
      caseSensitive: false,
      includeCollections: true,
    };
    collectionBrowser.threshold = SalesToolkit.Theme.getIntegerProperty(
      "collectionBrowserSearchThreshold",
      60
    );
    collectionBrowser.pageSize = SalesToolkit.Theme.getIntegerProperty(
      "collectionBrowserSearchPageSize",
      12
    );
    collectionBrowser.assetPageSize = SalesToolkit.Theme.getIntegerProperty(
      "collectionBrowserAssetPageSize",
      20
    );

    // Attach event listeners to the collection browser
    collectionBrowser.on(
      "item-added-to-favourites",
      _updateItemCartAndFavouritesTagsAndReturnFalse
    );
    collectionBrowser.on(
      "item-removed-from-favourites",
      _updateItemCartAndFavouritesTagsAndReturnFalse
    );
    collectionBrowser.on(
      "item-added-to-cart",
      _updateItemCartAndFavouritesTagsAndReturnFalse
    );
    collectionBrowser.on(
      "item-removed-from-cart",
      _updateItemCartAndFavouritesTagsAndReturnFalse
    );

    collectionBrowser.on("search", function () {
      if (
        !collectionBrowser.searchTerm ||
        collectionBrowser.searchTerm.length == 0
      ) {
        if (loadedHeaderId && loadedHeaderId.length > 0)
          $("#collectionHeader").show();
        $("#topLevelFilters").css("display", "flex");

        if (
          SalesToolkit.Theme.getBooleanProperty("useCalview", false) &&
          localStorage.calViewActive == "true"
        ) {
          $("body").addClass("showing-cal-view");
        } else {
          $("body").removeClass("showing-cal-view");
        }
        $("body").removeClass("searching");
      } else {
        $("#collectionHeader").hide();
        $("#topLevelFilters").hide();
        $("body").removeClass("showing-cal-view");
        $("body").addClass("searching");
      }
      _updateURLsAndPageButtonLinks();
    });

    collectionBrowser.on("filterPageAdded", function () {
      _updateURLsAndPageButtonLinks();
    });

    collectionBrowser.on("page-change", function(){ 
      _updateURLsAndPageButtonLinks();
      if(SalesToolkit.Theme.getBooleanProperty("scrollToTopOnPageChange", false)){
       const element = document.getElementById('main-div-center');
        if (element) {
            element.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
        }
      }
    });

    collectionBrowser.on("info-requested", function (item) {
      _parent.showInformationForItem(item); // Assumes global function
    });

    collectionBrowser.on("item-added", function (item) {
      const itemElement = document.querySelector(
        '#atheneum-home-page [data-atheneum-item-id="' + item.id + '"]'
      );
      if (!itemElement) return;

      let hiddenDueToPagination = false;
      const itemNumber =
        document.querySelector("#atheneum-home-page").children.length + 1;

      if (collectionBrowser.searchTerm) {
        hiddenDueToPagination =
          Math.floor(itemNumber / collectionBrowser.pageSize) !=
          (collectionBrowser.currentPage || 1);
      } else if (collectionBrowser.totalPages > 1) {
        hiddenDueToPagination =
          Math.floor(itemNumber / collectionBrowser.assetPageSize) + 1 !=
          (collectionBrowser.currentPage || 1);
      }

      itemElement.classList.toggle(
        "atheneum-item-pagination-hidden",
        hiddenDueToPagination
      );
      itemElement.classList.toggle(
        "atheneum-item-pagination-shown",
        !hiddenDueToPagination
      );

      if (!hiddenDueToPagination) {
        const infoIconElement = itemElement.querySelector(
          ".atheneum-item .asset-style .info-btn img"
        );
        if (infoIconElement) {
          if (
            SalesToolkit.Theme.getBooleanProperty(
              "hideAssetInformationButton",
              false
            )
          ) {
            infoIconElement.style.display = "none";
          } else {
            _parent.applyImageToElement(
              infoIconElement,
              "assetInformationIcon",
              "img/default/info_icon.svg"
            );
            infoIconElement.style.display = "block";
          }
        }

        const thumbnailElement = itemElement.querySelector(
          '.atheneum-item[data-atheneum-item-type="collection"] .collection-style .thumbnail-image, .atheneum-item[data-atheneum-item-type="asset"] .asset-style .thumbnail'
        );
        if (thumbnailElement && !thumbnailElement.style.backgroundImage) {
          const bgImageURL = thumbnailElement.getAttribute(
            "data-background-image-url"
          );
          if (
            bgImageURL &&
            !thumbnailElement.classList.contains(
              "data-background-update-queued"
            )
          ) {
            thumbnailElement.classList.add("data-background-update-queued");
            _parent.addUrlToRewriteQueue(bgImageURL, function (rewrittenURL) {
              if (rewrittenURL != null && rewrittenURL.href)
                rewrittenURL = rewrittenURL.href;

              if (
                rewrittenURL !== null &&
                rewrittenURL.includes("https:") &&
                !rewrittenURL.includes(
                  "keepRatio=true&shouldCrop=false&width=800&height=450"
                )
              ) {
                rewrittenURL =
                  rewrittenURL +
                  "?keepRatio=true&shouldCrop=false&width=800&height=450";
              }

              if (
                rewrittenURL !== null &&
                (rewrittenURL.includes(".index") ||
                  !rewrittenURL.includes("file:"))
              ) {
                thumbnailElement.style.backgroundImage =
                  "url('" + bgImageURL + "')";
              } else {
                thumbnailElement.style.backgroundImage =
                  "url('" + rewrittenURL + "')";
              }
              thumbnailElement.style.opacity = 1;
            });
          }
        }
      }
      _updateItemCartAndFavouritesTagsAndReturnFalse(item);
    });

    collectionBrowser.on("refresh", function () {
      if (
        collectionBrowser.totalPages <= 1 &&
        !collectionBrowser.searchTerm &&
        !collectionBrowser.filtered
      ) {
        $(".collection-browser-pagination").removeClass("shown");
      }
      _renderCollectionBrowserPlugins();
    });

    let previousCollectionId = null;
    collectionBrowser.on("path-change", function (path, ignoreTracking) {
      const breadcrumbs = document.getElementById("salestoolkit-breadcrumbs");
      const currentCollectionId =
        collectionBrowser?.path?.length > 0
          ? collectionBrowser.path[collectionBrowser.path.length - 1]
          : SalesToolkit.getHomeCollectionID();
      const searchInput = document.getElementById("search");
      const searchValue = searchInput?.value || "";

      const urlParams = new URLSearchParams(window.location.search);

      if (
        searchValue.length === 0 &&
        typeof collectionBrowser.searchTerm === "undefined" &&
        (!currentCollectionId ||
          currentCollectionId === SalesToolkit.getHomeCollectionID())
      ) {
        urlParams.delete("collectionId");
        $("#topLevelFilters").css("display", "flex");
        if (
          SalesToolkit.Theme.getBooleanProperty("useCalview", false) &&
          localStorage.calViewActive == "true"
        ) {
          $("body").addClass("showing-cal-view");
        } else {
          $("body").removeClass("showing-cal-view");
        }
        $("body").removeClass("searching");
      } else {
        const aopFilterName = SalesToolkit.Translation.get(
          "aopFilterName",
          "AOP"
        );
        if (collectionBrowser.activeTopLevelFilter !== aopFilterName) {
          $("#topLevelFilters").hide();
        }
        if (currentCollectionId) {
          urlParams.set("collectionId", currentCollectionId);
          _calViewLoadCollection(currentCollectionId);
        } else {
          urlParams.delete("collectionId");
        }
      }

      const newQueryString = _cleanLoginURLParams(urlParams.toString());
      const newUrl = `${window.location.pathname}${newQueryString}`;
      window.history.replaceState({ path: newUrl }, "", newUrl);

      SalesToolkit.Theme.setOverridingCollection(currentCollectionId || null);
      _parent.setupContactInfoButton();
      _parent.renderSalesToolkitLanguages();
      localStorage["salestoolkitCollectionThemeOverrideId"] =
        currentCollectionId;

      if (
        currentCollectionId &&
        SalesToolkit.Library.collections[currentCollectionId]?.assets.length ===
          1 &&
        SalesToolkit.Theme.getBooleanProperty("autoPreviewSingleAssets", false)
      ) {
        const assetId =
          SalesToolkit.Library.collections[currentCollectionId].assets[0].id;
        const asset = SalesToolkit.Library.assets[assetId];
        if (
          asset.canPreview &&
          asset.mimetype === "asset/url" &&
          !SalesToolkit.Theme.getBooleanProperty("useIframeForAssetUrls", false)
        ) {
          SalesToolkit.Pages.openInNewTab(asset.url);
          if (
            SalesToolkit.Theme.getBooleanProperty(
              "hitBackOnLoadingURLExternally",
              true
            )
          ) {
            _onHomePageBackButtonClick();
            path = collectionBrowser?.path;
          }
        } else if (!asset.canPreview) {
          console.log("Asset can't be previewed");
        } else {
          const urlParams = _getUrlVars();
          const cleanedURLParams = _cleanLoginURLParams(urlParams); // Assumes global function
          //This is no longer needed - loadAssetScreen adds it cleanedURLParams.assetId = assetId;
          _parent.navigator.loadAssetScreen(assetId, cleanedURLParams);
        }
      }

      _loadArticleHeader(currentCollectionId); // Assumes global function
      _parent.addFormsToSideMenu(); // Assumes global function
      _initializeIconVariables();
      _renderCollectionBrowserPlugins();

      if (currentCollectionId != previousCollectionId) {
        _parent.applyThemeToToolkit(); // Assumes global function
        previousCollectionId = currentCollectionId;
        _scrollToTop();
      }

      if (treeView && currentCollectionId)
        treeView.selectCollection(currentCollectionId);
      if (treeView) treeView.setPath(path);

      if (SalesToolkit.Theme.getBooleanProperty("scrollTreeView", false)) {
        setTimeout(function () {
          if (!document.body.classList.contains("module-index")) {
            return;
          }
          const mainDivBottom =
            $("#main-panel").offset().top + $("#main-panel").outerHeight();
          const treeViewEl = $("#atheneum-tree-view");
          let height = mainDivBottom - treeViewEl.offset().top - 12;
          treeViewEl.height(height);
          treeViewEl.css("overflow-y", "scroll");
          treeViewEl
            .parents(".portlet.light")
            .css({ "padding-bottom": 0, "margin-bottom": 0 });
        }, 500);
      }

      while (breadcrumbs.firstElementChild != breadcrumbs.lastChild) {
        breadcrumbs.removeChild(breadcrumbs.lastChild);
      }

      const homekey = document.getElementById("homekey");
      if (path.length > 0) {
        homekey.innerHTML =
          SalesToolkit.Library.collections[path[path.length - 1]].name;
      } else {
        homekey.innerHTML = SalesToolkit.Translation.getOrGetThemeKey(
          "mainPanel",
          "Home"
        );
      }

      const backbuttonhomehide = document.getElementById(
        "breadcrumbsbuttonhide"
      );
      const isAtRoot =
        path.length < 1 ||
        (path.length == 1 &&
          collectionBrowser.topLevelSectioned &&
          path[0] === collectionBrowser.topLevelSectionId) ||
        (path.length == 1 &&
          SalesToolkit.getHomeCollectionID() != null &&
          SalesToolkit.getHomeCollectionID() == path[0]);
      backbuttonhomehide.style.visibility = isAtRoot ? "hidden" : "visible";

      if (!path) return;

      for (let i = 0; i < path.length; i++) {
        if (
          SalesToolkit.getHomeCollectionID() != null &&
          SalesToolkit.getHomeCollectionID() == path[i]
        ) {
          continue;
        }
        const collection = SalesToolkit.Library.collections[path[i]];
        if (collection) {
          // Breadcrumb rendering logic...
          breadcrumbs.appendChild(
            document.createElement("LI")
          ).innerHTML = `<i class="fa fa-angle-right"></i><a class="breadsmall" data-action="breadcrumbClick" data-collection-id="${collection.id}" href="#">${collection.name}</a>`;
        }
      }

      if (ignoreTracking === true) {
        return;
      }

      if (salestoolkitCollectionViewTracking !== null) {
        SalesToolkit.Tracking.finish(salestoolkitCollectionViewTracking);
        salestoolkitCollectionViewTracking = null;
      }
      if (path.length > 0) {
        salestoolkitCollectionViewTracking =
          SalesToolkit.Tracking.startTrackingCollectionView(
            path[path.length - 1]
          );
      }
    });

    collectionBrowser.ignoreLastPathStorage = true;
    collectionBrowser.trigger("path-change", collectionBrowser.path);

    collectionBrowser.on("item-action-view-click", function (item) {
      if (item.type == "asset") {
        const asset = SalesToolkit.Library.assets[item.id];
        if (!asset || !asset.canPreview) return false;
        if (
          asset.mimetype == "asset/url" &&
          !SalesToolkit.Theme.getBooleanProperty("useIframeForAssetUrls", false)
        ) {
          SalesToolkit.Pages.openInNewTab(asset.url);
          return false;
        }
      }
    });

    //END event listeners
    // --- Post-Collection Browser Initialization ---
    _setupCalViewBridge();

    const useCalview = SalesToolkit.Theme.getBooleanProperty(
      "useCalview",
      false
    );
    let calViewActive = localStorage.calViewActive === "true";

    if (
      !calViewActive &&
      SalesToolkit.Theme.getBooleanProperty("useCalViewByDefault", true)
    ) {
      calViewActive = localStorage.getItem("calViewActive") === null;
      if (calViewActive) localStorage.setItem("calViewActive", "true");
    }

    let lastPath = localStorage.getItem("lastCBPath");
    const loadHome = _getUrlParam("loadHome", "false") === "true";

    if (loadHome) {
      lastPath = null;
      delete localStorage.searchTerm;
      _clearAllFilters();
    }

    if (
      !SalesToolkit.Theme.getBooleanProperty("viewAssetBackUsesHistory", false)
    ) {
      lastPath = null;
    }

    const urlParamLoadCB = _getUrlParam("loadCB", "0") === "1";

    if (!SalesToolkit.CrossPlatform.enabled) {
      const query = _cleanLoginURLParams(window.location.search);
      window.history.pushState({}, document.title, `/index.html${query}`);
    }

    if (!collectionId && (lastPath || urlParamLoadCB)) {
      collectionBrowser.loadStoredPath();
    } else if (collectionId) {
      collectionBrowser.viewCollection(collectionId);
    }

    if (useCalview) {
      $("body").removeClass("no-cal-view");
      if (calViewActive) $("body").addClass("showing-cal-view");

      const calViewIframe = document.getElementById("atheneum-calview-ui");
      if (calViewIframe) {
        calViewIframe.onload = function () {
          if (calViewIframe.contentWindow) {
            console.log("calView.html iframe loaded. Attaching bridge.");
            // This is the key step: we add our bridge object directly to the iframe's window.
            calViewIframe.contentWindow.calViewBridge = _calViewBridge;

            // You might need to manually trigger the 'ready' function inside the iframe
            // if its own 'DOMContentLoaded' already fired.
            if (
              typeof calViewIframe.contentWindow.ready === "function" &&
              calViewIframe.contentWindow.document.readyState === "complete"
            ) {
              calViewIframe.contentWindow.initCalview();
            }
          }
        };
      }
      $("#atheneum-calview-ui").attr(
        "src",
        SalesToolkit.Theme.getStringProperty(
          "calViewHtmlFileName",
          "calView.html"
        )
      );

      _resizeCalView();
    } else {
      $("body").removeClass("showing-cal-view").addClass("no-cal-view");
    }

    const homekeyElement = document.getElementById("homekey");
    if (homekeyElement) {
      if (collectionBrowser.path.length > 0) {
        const currentCollectionId =
          collectionBrowser.path[collectionBrowser.path.length - 1];
        homekeyElement.innerHTML =
          SalesToolkit.Library.collections[currentCollectionId].name;
      } else {
        homekeyElement.innerHTML = SalesToolkit.Translation.getOrGetThemeKey(
          "mainPanel",
          "Home"
        );
      }
    }

    if (localStorage.searchTerm) {
      _setSearchFromLocalStorage();
    }
  }
  //#endregion

  //#region Tree View Management
  /**
   * Initializes the tree view component and its event listeners.
   */
  function _setupTreeView() {
    treeView = SalesToolkit.Atheneum.UI.TreeView(
      document.getElementById("atheneum-tree-view"),
      SalesToolkit.getHomeCollectionID()
    );

    let collectionId =
      SalesToolkit.Pages.CurrentPage.getQueryParam("collectionId") || null;

      if(!_viewCollectionId)
    _viewCollectionId =
      SalesToolkit.Pages.CurrentPage.getQueryParam("viewCollection");
    if (
      _viewCollectionId &&
      SalesToolkit.Library.collections[_viewCollectionId]
    ) {
      // Store/Override the collectionId so all subsequent logic (Journal tabs, filters, initial view) uses this
      collectionId = _viewCollectionId;
    }

    if (collectionId) {
      treeView.selectCollection(collectionId);
    }

    treeView.mirror(collectionBrowser);

    treeView.on("item-click", function (item) {
      if (item.type == "collection") {
        if (
          collectionBrowser.path.length == 0 ||
          collectionBrowser.path[collectionBrowser.path.length - 1] != item.id
        ) {
          treeView.selectCollection(item.id);
          collectionBrowser.viewCollection(item.id);
        }

        if (SalesToolkit.Theme.getBooleanProperty("scrollTreeView", false)) {
          setTimeout(function () {
            const $treeViewElement = $("#atheneum-tree-view");
            $treeViewElement.scrollTop(0);
            const li = $("#" + treeView.puid + "_collection_" + item.id);
            if (li.length === 0) return;

            let offsetTop = li.offset().top;
            let parentOffset = li.offsetParent();
            while (parentOffset.offset().top > 0) {
              offsetTop -= parentOffset.offset().top;
              parentOffset = parentOffset.offsetParent();
            }
            offsetTop -= 7; // Adjustment
            if (offsetTop < 0) offsetTop = 0;
            $treeViewElement.scrollTop(offsetTop);
          }, 350);
        }
      } else if (item.type == "asset") {
        const asset = SalesToolkit.Library.assets[item.id];
        if (!asset || !asset.canPreview) {
          return false;
        }

        if (
          asset.mimetype == "asset/url" &&
          !SalesToolkit.Theme.getBooleanProperty("useIframeForAssetUrls", false)
        ) {
          SalesToolkit.Pages.openInNewTab(asset.url);
          return false;
        }

        _parent.navigator.loadAssetScreen(item.id);
        return;
      }
      return false;
    });

    $(treeView.element).on("loaded.jstree", function () {
      isTreeReadyForRepopulation = true;
      treeView.selectCollection(
        collectionBrowser.path
          ? collectionBrowser.path[collectionBrowser.path.length - 1]
          : undefined
      );
      treeView.setPath(collectionBrowser.path);
      _scrollToTop();
    });

    treeView.setPath(collectionBrowser.path);
    isTreeReadyForRepopulation = false;
    treeView.populateTree();
  }
  //#endregion

  //#region Sync & Library Updates
  /**
   * Checks if the library update should be ignored based on global flags.
   * @returns {boolean} - True if the update should be ignored.
   */
  function _checkIgnoreUpdate() {
    if (SalesToolkit.LocalStorage.db && _parent.getIgnoreUpdate()) {
      // Assumes global ignoreUpdate flag
      librarySyncInProgress = false;
      const cacheState = document.getElementById("salestoolkit-cache-state");
      if (cacheState) {
        cacheState.className = "badge salestoolkit-cache-state-online";
        cacheState.innerHTML = SalesToolkit.Translation.get(
          "online",
          "online"
        ).toUpperCase();
      }

      if (SalesToolkit.Theme.getBooleanProperty("useFlatLibrary", false)) {
        document.getElementById(
          "salestoolkit-cache-collections-total-label"
        ).innerHTML = SalesToolkit.Translation.get(
          "collectionsTotalItemsMode",
          "Total Items"
        );
        document.getElementById(
          "salestoolkit-cache-collections-downloaded-label"
        ).innerHTML = SalesToolkit.Translation.get(
          "collectionsCachedItemsMode",
          "Items Cached"
        );
        const totalItems =
          Object.values(SalesToolkit.Library.collections).filter((x) =>
            SalesToolkit.User.canSee(x)
          ).length +
          Object.values(SalesToolkit.Library.assets).filter((x) =>
            SalesToolkit.User.canSee(x)
          ).length;
        document.getElementById(
          "salestoolkit-cache-collections-total"
        ).innerHTML = totalItems;
        document.getElementById(
          "salestoolkit-cache-collections-downloaded"
        ).innerHTML = totalItems;
      } else {
        const collectionCount = Object.values(
          SalesToolkit.Library.collections
        ).filter((x) => SalesToolkit.User.canSee(x)).length;
        document.getElementById(
          "salestoolkit-cache-collections-total"
        ).innerHTML = collectionCount;
        document.getElementById(
          "salestoolkit-cache-collections-downloaded"
        ).innerHTML = collectionCount;
      }

      document.getElementById(
        "salestoolkit-cache-progress-percentage"
      ).innerHTML = "100%";
      document.getElementById("salestoolkit-cache-progress-bar").style.width =
        "100%";
      return true;
    } else {
      return false;
    }
  }

  /**
   * Waits for the library to be fully loaded before initiating the update check.
   * @param {object} libraryUpdateCallbacks - The callbacks for the library update process.
   */
  function _callLibraryWhenReady(libraryUpdateCallbacks) {
    if (!SalesToolkit.Library.fullyLoaded) {
      if (callLibraryReadyTimeout) clearTimeout(callLibraryReadyTimeout);
      callLibraryReadyTimeout = setTimeout(
        () => _callLibraryWhenReady(libraryUpdateCallbacks),
        1000
      );
      return;
    }

    if (SalesToolkit.Theme.getBooleanProperty("precheckLibrary", false)) {
      SalesToolkit.API.checkLibraryTotals({
        success: function (json) {
          let itemsMode = json.hasOwnProperty("totalItems");
          let collections = Object.values(
            SalesToolkit.Library.collections
          ).filter((x) => SalesToolkit.User.canSee(x));
          let emptyCollections = Object.values(
            SalesToolkit.Library.collections
          ).filter(
            (x) =>
              !SalesToolkit.User.canSee(x) &&
              x.stories.length == 0 &&
              x.assets.length == 0
          );
          let collectionCount = collections.length + emptyCollections.length;

          if (itemsMode) {
            collectionCount += Object.values(
              SalesToolkit.Library.assets
            ).filter((x) => SalesToolkit.User.canSee(x)).length;
            if (!json.totalItemsIgnoringTimestamp && json.totalItems != 0) {
              json.totalItemsIgnoringTimestamp = json.totalItems;
            }
          }

          let progressValue = 0;
          if (itemsMode) {
            if (json.totalItemsIgnoringTimestamp == collectionCount) {
              progressValue =
                (collectionCount - json.totalItems) / collectionCount;
            } else {
              progressValue =
                collectionCount / json.totalItemsIgnoringTimestamp;
            }
          } else {
            progressValue = collectionCount / json.totalStoryCount;
          }

          if (
            progressValue < 1 &&
            SalesToolkit.Theme.getBooleanProperty(
              "blockUserWhenSyncingNewItems",
              false
            )
          ) {
            let itemsUpdated;
            if (json.totalItems == json.totalItemsIgnoringTimestamp) {
              itemsUpdated = collectionCount;
            } else {
              itemsUpdated = collectionCount - json.totalItems;
            }
            _showNewInitialSync(
              progressValue,
              itemsUpdated,
              itemsMode
                ? json.totalItemsIgnoringTimestamp
                : json.totalStoryCount
            );
            setTimeout(function () {
              SalesToolkit.Library.update(libraryUpdateCallbacks);
            }, 300);
          } else if (!_checkIgnoreUpdate()) {
            SalesToolkit.Library.update(libraryUpdateCallbacks);
          }
        },
        failure: function (reason, httpStatus, errorCode) {
          if (!_checkIgnoreUpdate()) {
            SalesToolkit.Library.update(libraryUpdateCallbacks);
          }
        },
      });
    } else if (!_checkIgnoreUpdate()) {
      SalesToolkit.Library.update(libraryUpdateCallbacks);
    }
  }

  /**
   * Handles UI updates specific to the index page after the main library is loaded.
   * This includes refreshing the collection browser and building/updating the filters.
   * @private
   */
  function _onLibraryLoaded() {
    if (collectionBrowser) {
      collectionBrowser.refresh();
    }

    let filtersRendered = false;
    let filters = SalesToolkit.Filters.getFilters();
    if (filters?.length > 0) {
      filtersRendered = true;

      if (
        filters &&
        filters.length > 0 &&
        SalesToolkit.Theme.getBooleanProperty("useFilters", false)
      ) {
        _buildFilters(); // Calls local function
        $(".filterdropdown").css("display", "flex");
        $(".filterdropdown").removeClass("disabled");
        $(".filters-row").show();
      } else {
        $(".filterdropdown").hide();
      }

      $("#lfclRoller").hide();
    }

    if (_parent.getIgnoreUpdate()) {
      // This global is still used elsewhere for now
      $("#lfclRoller").hide();
      return;
    }

    SalesToolkit.Filters.update({
      success: function (json) {
        if (!filtersRendered || json.filters?.length > 0) {
          let filters = SalesToolkit.Filters.getFilters();
          if (
            filters &&
            filters.length > 0 &&
            SalesToolkit.Theme.getBooleanProperty("useFilters", false)
          ) {
            _buildFilters(); // Calls local function
            $(".filterdropdown").css("display", "flex");
            $(".filterdropdown").removeClass("disabled");
            $(".filters-row").show();
          } else {
            $(".filterdropdown").hide();
          }
        }
        $("#lfclRoller").hide();
      },
      failure: function (reason, httpStatus, errorCode) {
        if (!filtersRendered) {
          let filters = SalesToolkit.Filters.getFilters();
          if (
            filters &&
            filters.length > 0 &&
            SalesToolkit.Theme.getBooleanProperty("useFilters", false)
          ) {
            _buildFilters(); // Calls local function
            $(".filterdropdown").css("display", "flex");
            $(".filterdropdown").removeClass("disabled");
            $(".filters-row").show();
          } else {
            $(".filterdropdown").hide();
          }
        }
        $("#lfclRoller").hide();
      },
    });
  }

  function repopulateWhenReady() {
    if (isTreeReadyForRepopulation) {
      treeView.populateTree();
      repopulationScheduled = false;
    } else if (repopulationScheduled) {
      return;
    } else {
      repopulationScheduled = true;
      // If the initial tree isn't ready, wait 50ms and check again
      setTimeout(repopulateWhenReady, 50);
    }
  }

  /**
   * Sets up the library update callbacks and initiates the update process.
   */
  function _setupLibraryUpdates() {
    const libraryUpdateCallbacks = {
      success: function () {
        _hideNewInitialSync(); // Assumes global function

        if (!_viewCollectionLoaded &&
          _viewCollectionId &&
          SalesToolkit.Library.collections[_viewCollectionId]
        ) {
          collectionBrowser.viewCollection(_viewCollectionId, true);
          treeView.populateTree();
        } else if (!collectionBrowser.topLevelSectioned) {
          collectionBrowser.viewCollection(
            collectionBrowser.path[collectionBrowser.path.length - 1] || null,
            false,
            true
          );

          treeView.refresh();
          repopulateWhenReady();
        }

        SalesToolkit.UI.Cart.updateMenuCartBadge();
        _buildFilters();
        _setupCalViewTabs(true);

        if (
          SalesToolkit.Theme.getBooleanProperty("useCalview", false) &&
          localStorage.calViewActive == "true"
        ) {
          _calViewReloadConditional();
        }

        librarySyncInProgress = false;
        webCacheIcon.firstElementChild.classList.remove("fa-spin");
        document.getElementById("salestoolkit-cache-state").className =
          "badge salestoolkit-cache-state-online";
        document.getElementById("salestoolkit-cache-state").innerHTML =
          SalesToolkit.Translation.get("online", "online").toUpperCase();

        if (SalesToolkit.Library.fullyLoaded) {
          document.getElementById(
            "salestoolkit-cache-progress-percentage"
          ).innerHTML = "100%";
          document.getElementById(
            "salestoolkit-cache-progress-bar"
          ).style.width = "100%";
        }
      },
    };

    const webCacheIcon = document.getElementById("salestoolkit-cache-icon");
    webCacheIcon.firstElementChild.classList.remove("fa-spin");
    document.getElementById("salestoolkit-cache-state").className =
      "badge salestoolkit-cache-state-online";
    document.getElementById("salestoolkit-cache-collections-total").innerHTML =
      SalesToolkit.Translation.get("calculating", "Calculating");
    document.getElementById(
      "salestoolkit-cache-collections-downloaded"
    ).innerHTML = Object.values(SalesToolkit.Library.collections).filter((x) =>
      SalesToolkit.User.canSee(x)
    ).length;
    document.getElementById(
      "salestoolkit-cache-progress-percentage"
    ).innerHTML = "0%";
    document.getElementById("salestoolkit-cache-progress-bar").style.width =
      "0%";
    document.getElementById("salestoolkit-cache-state").innerHTML =
      SalesToolkit.Translation.get("online", "online").toUpperCase();

    if (
      (!SalesToolkit.CrossPlatform.enabled &&
        SalesToolkit.Theme.getBooleanProperty(
          "salestoolkitTilesProgressivelyUpdate",
          false
        )) ||
      (SalesToolkit.Theme.getBooleanProperty(
        "salestoolkitTilesProgressivelyUpdateOnFirstSync",
        false
      ) &&
        SalesToolkit.Library.getTopLevelCollections().length == 0)
    ) {
      libraryUpdateCallbacks.progress = function (json) {
        if (typeof collectionBrowser.searchTerm !== "undefined") return;

        if (json === "progressStart") {
          const collections = Object.values(
            SalesToolkit.Library.collections
          ).filter((x) => SalesToolkit.User.canSee(x));
          if (collections.length === 0) {
            _showNewInitialSync(0, 0, 0); // Assumes global function
          }
          return;
        }

        let itemsMode = json.hasOwnProperty("totalItems");
        let collections = Object.values(
          SalesToolkit.Library.collections
        ).filter((x) => SalesToolkit.User.canSee(x));
        let emptyCollections = Object.values(
          SalesToolkit.Library.collections
        ).filter(
          (x) =>
            !SalesToolkit.User.canSee(x) &&
            x.stories.length == 0 &&
            x.assets.length == 0
        );
        let collectionCount = collections.length + emptyCollections.length;

        if (itemsMode) {
          collectionCount += Object.values(SalesToolkit.Library.assets).filter(
            (x) => SalesToolkit.User.canSee(x)
          ).length;
          if (!json.totalItemsIgnoringTimestamp && json.totalItems != 0) {
            json.totalItemsIgnoringTimestamp = json.totalItems;
          }
          document.getElementById(
            "salestoolkit-cache-collections-total-label"
          ).innerHTML = SalesToolkit.Translation.get(
            "collectionsTotalItemsMode",
            "Total Items"
          );
          document.getElementById(
            "salestoolkit-cache-collections-downloaded-label"
          ).innerHTML = SalesToolkit.Translation.get(
            "collectionsCachedItemsMode",
            "Items Cached"
          );
        }

        let progressValue = 0;
        if (itemsMode) {
          if (json.totalItemsIgnoringTimestamp == json.totalItems) {
            progressValue = collectionCount / json.totalItemsIgnoringTimestamp;
          } else {
            progressValue =
              (collectionCount - json.totalItems) / collectionCount;
          }
        } else {
          progressValue = collectionCount / json.totalStoryCount;
        }

        if (progressValue < 1) {
          collectionBrowser.viewCollection(
            collectionBrowser.path[collectionBrowser.path.length - 1] || null
          );
          if (
            SalesToolkit.Theme.getBooleanProperty("useCalview", false) &&
            localStorage.calViewActive == "true"
          ) {
            _calViewReloadConditional();
          }
        }

        SalesToolkit.UI.Cart.updateMenuCartBadge();
        document.getElementById("salestoolkit-cache-state").className =
          "badge salestoolkit-cache-state-syncing";
        document.getElementById("salestoolkit-cache-state").innerHTML =
          SalesToolkit.Translation.get("syncing", "syncing").toUpperCase();
        document.getElementById(
          "salestoolkit-cache-collections-total"
        ).innerHTML = itemsMode
          ? json.totalItemsIgnoringTimestamp
          : json.totalStoryCount;
        document.getElementById(
          "salestoolkit-cache-collections-downloaded"
        ).innerHTML = collectionCount;
        document.getElementById(
          "salestoolkit-cache-progress-percentage"
        ).innerHTML = Math.floor(progressValue * 100) + "%";
        $("#salestoolkit-cache-progress-bar").css(
          "width",
          progressValue * 100 + "%"
        );

        librarySyncInProgress = itemsMode
          ? json.totalItems > 0 ||
            collectionCount < json.totalItemsIgnoringTimestamp
          : collectionCount < json.totalStoryCount;

        if (!librarySyncInProgress) {
          webCacheIcon.firstElementChild.classList.remove("fa-spin");
          document.getElementById("salestoolkit-cache-state").className =
            "badge salestoolkit-cache-state-online";
          document.getElementById("salestoolkit-cache-state").innerHTML =
            SalesToolkit.Translation.get("online", "online").toUpperCase();
        } else {
          webCacheIcon.firstElementChild.classList.add("fa-spin");
          if (
            SalesToolkit.Theme.getBooleanProperty(
              "blockUserWhenSyncingNewItems",
              false
            )
          ) {
            let itemsUpdated =
              json.totalItems == json.totalItemsIgnoringTimestamp
                ? collectionCount
                : collectionCount - json.totalItems;
            _showNewInitialSync(
              progressValue,
              itemsUpdated,
              itemsMode
                ? json.totalItemsIgnoringTimestamp
                : json.totalStoryCount
            );
          }
        }
        console.log(
          `library update. page ${json.pageIndex + 1 || 0} of ${
            json.totalPages
          }. collections: ${collectionCount - (json.totalItems || 0)} / ${
            itemsMode ? json.totalItemsIgnoringTimestamp : json.totalStoryCount
          }`
        );
      };
    } else if (!SalesToolkit.CrossPlatform.enabled) {
      $(".syncbuttontopbar").hide();
      librarySyncInProgress = false;
      _hideNewInitialSync();
    } else {
      librarySyncInProgress = false;
      _hideNewInitialSync();
    }

    _callLibraryWhenReady(libraryUpdateCallbacks);
  }
  //#endregion

  //#region Initial Sync & Visit Tracking
  /**
   * Hides the initial sync modal.
   */
  function _hideNewInitialSync() {
    if (typeof $("#salestoolkit-initial-sync-modal").modal == "undefined") {
      if (hideSyncModalTimeout) clearTimeout(hideSyncModalTimeout);
      hideSyncModalTimeout = setTimeout(_hideNewInitialSync, 100);
      return;
    }
    $("#sync-blocker").hide();
    $("#salestoolkit-initial-sync-modal").modal("hide");
  }

  /**
   * Shows and updates the initial sync modal with the current progress.
   * @param {number} progressValue - The progress value from 0 to 1.
   * @param {number} collectionCount - The number of items downloaded.
   * @param {number} totalStoryCount - The total number of items to download.
   */
  function _showNewInitialSync(
    progressValue,
    collectionCount,
    totalStoryCount
  ) {
    $("#sync-blocker").show();
    let percentageDisplay = document.getElementById(
      "salestoolkit-initial-sync-progress-percentage"
    );
    percentageDisplay.innerHTML = Math.floor(progressValue * 100) + "%";
    document.getElementById(
      "salestoolkit-initial-sync-progress-bar"
    ).style.width = progressValue * 100 + "%";

    if (SalesToolkit.Theme.getBooleanProperty("useFlatLibrary", false)) {
      document.getElementById(
        "salestoolkit-initial-sync-progress-text"
      ).innerHTML = SalesToolkit.Translation.getFormatted(
        "initialSyncItemsData",
        "Downloaded {0} of {1} items",
        [collectionCount, totalStoryCount]
      );
    } else {
      document.getElementById(
        "salestoolkit-initial-sync-progress-text"
      ).innerHTML = SalesToolkit.Translation.getFormatted(
        "initialSyncCollectionData",
        "Downloaded {0} of {1} collections",
        [collectionCount, totalStoryCount]
      );
    }

    if (totalStoryCount === 0) {
      document.getElementById(
        "salestoolkit-initial-sync-progress-text"
      ).innerHTML = SalesToolkit.Translation.get(
        "initialSyncItemsFirstSyncStarted",
        "Starting initial sync"
      );
    }

    let initialProgressColour = _parent.colourString(
      SalesToolkit.Theme.getColourProperty("salestoolkitSyncProgressColour")
    );
    let initialProgressBackground = _parent.colourString(
      SalesToolkit.Theme.getColourProperty(
        "salestoolkitSyncProgressBackgroundColour"
      )
    );

    document.getElementById(
      "salestoolkit-initial-sync-progress-bar"
    ).style.backgroundColor = initialProgressBackground;

    if (progressValue > 0.48) {
      percentageDisplay.style.color = initialProgressBackground;
    } else {
      percentageDisplay.style.color = initialProgressColour;
    }

    $("#initial-sync-title").text(
      SalesToolkit.Translation.get("syncingNewItemsTitle", "Sync in progress")
    );
    $("#initial-sync-body").text(
      SalesToolkit.Translation.get(
        "syncingNewItemsBody",
        "New items are being synced from the server, please wait until complete."
      )
    );

    if (!$("#salestoolkit-initial-sync-modal").hasClass("in"))
      $("#salestoolkit-initial-sync-modal").modal("show");
  }

  /**
   * Finishes the initial sync process by updating the library one last time and reloading the page.
   */
  function _salestoolkitFinishInitialSync() {
    if (_parent.getIgnoreUpdate()) return; // Assumes global ignoreUpdate flag
    SalesToolkit.Library.update({
      success: function () {
        window.location.reload();
      },
      progress: function (json) {
        const collectionCount = Object.keys(
          SalesToolkit.Library.collections
        ).length;
        console.log(
          `library post initial sync update. page ${json.pageIndex || 0} of ${
            json.totalPages
          }. collections: ${collectionCount} / ${json.totalStoryCount}`
        );
      },
      failure: function () {
        window.location.reload();
      },
    });
  }

  /**
   * Renders the progress of the initial asset sync.
   */
  function _renderInitialSync() {
    const percentageDisplay = document.getElementById(
      "salestoolkit-initial-sync-progress-percentage"
    );
    const progressBar = document.getElementById(
      "salestoolkit-initial-sync-progress-bar"
    );
    const progressText = document.getElementById(
      "salestoolkit-initial-sync-progress-text"
    );

    const progressPercentage =
      SalesToolkit.Cache.assetsTotal == 0
        ? "0%"
        : SalesToolkit.Cache.progressPercentage || "0%";
    percentageDisplay.innerHTML = progressPercentage;
    progressBar.style.width = progressPercentage;
    progressText.innerHTML = SalesToolkit.Cache.assetsTotal
      ? `Downloaded: ${SalesToolkit.Cache.assetsDownloaded || 0} of ${
          SalesToolkit.Cache.assetsTotal
        }`
      : "Calculating Total Missing...";

    progressBar.style.backgroundColor = SalesToolkit.Cache.offline
      ? "#B44"
      : "";

    setTimeout(function () {
      percentageDisplay.style.color =
        SalesToolkit.Cache.assetsTotal != 0 &&
        SalesToolkit.Cache.progressValue > 0.51
          ? "white"
          : "black";
      if (SalesToolkit.Cache.finished && !SalesToolkit.Cache.neverSynced) {
        _salestoolkitFinishInitialSync();
      }
    }, 500);
  }

  /**
   * Checks the initial sync status and starts the UI updates if required.
   */
  function _salestoolkitCheckInitialSync() {
    SalesToolkit.Cache.getSyncProgress(function (json) {
      if (
        (!(json && json.progress && json.progress.finished) &&
          SalesToolkit.Cache.unknown) ||
        (json && Object.keys(json).length == 0)
      ) {
        if (checkInitialSyncTimeout) clearTimeout(checkInitialSyncTimeout);

        checkInitialSyncTimeout = setTimeout(_salestoolkitCheckInitialSync, 50);
        return;
      }

      if (
        !SalesToolkit.Cache.neverSynced &&
        (SalesToolkit.Cache.finished || SalesToolkit.Cache.progressValue > 0.95)
      ) {
        SalesToolkit.Cache.initialSync = false;
        delete localStorage.initialSync;
      }

      if (SalesToolkit.Cache.initialSync && !SalesToolkit.Cache.finished) {
        localStorage.initialSync = "true";
        document.getElementById("salestoolkit-initiate-initial-sync").click();
        _renderInitialSync();
        salestoolkitFirstTimeSyncInterval = setInterval(
          _renderInitialSync,
          250
        );
        return true;
      }
    });
  }

  /**
   * Sets up the final pieces of the module including sync callbacks, news ticker, and visit tracking.
   */
  function _setupFinalCallbacksAndTracking() {
    SalesToolkit.NewsFeed.enableAutoUpdate(_updateTickerCallback);
    if (_parent.getIgnoreUpdate()) {
      SalesToolkit.NewsFeed.update(_updateTickerCallback);
    }

    SalesToolkit.Cache.onSyncFinished = function () {
      treeView.refresh();
      repopulateWhenReady();
      collectionBrowser.refresh();
      SalesToolkit.UI.Cart.updateMenuCartBadge();
    };

    _salestoolkitCheckInitialSync();

    _renderCollectionBrowserPlugins();

    const visitTime = SalesToolkit.User.getVisit();
    if (visitTime == null) {
      SalesToolkit.User.startVisit();
    } else if (86400000 < new Date().getTime() - parseInt(visitTime)) {
      SalesToolkit.User.endVisit();
      SalesToolkit.User.startVisit();
    }
  }
  //#endregion

  //#region Article Headers
  function _preloadArticleHeaderCss() {
    let $head = $("head"); // Uses local jQuery
    // Uses local SalesToolkit
    let articleCss = SalesToolkit.Theme.getStringProperty("articleHeadersCss");
    if (
      !articleCss ||
      typeof articleCss !== "string" ||
      !articleCss.startsWith("http")
    ) {
      return;
    }

    $.ajax({
      url: articleCss,
      type: "GET",
      success: function (data) {
        let cssStyle = document.createElement("STYLE");
        cssStyle.setAttribute("type", "text/css");
        cssStyle.setAttribute("id", "articleCssPreload");
        cssStyle.innerHTML = data;
        $head.append(cssStyle);
      },
      error: function (data) {
        console.error("Failed to preload article header CSS.");
      },
    });
  }

  function _clearArticleHeader() {
    let $header = $("#collectionHeader");
    $header.empty();
    $header.css("height", 0);
    loadedHeaderId = ""; // Sets the local variable
  }

  function _loadArticleHeader(collectionId) {
    let $header = $("#collectionHeader");
    if (loadedHeaderId === collectionId && $header.height() !== 0) {
      return;
    }

    $header.empty();
    $header.css("height", 0);

    let collection = SalesToolkit.Library.collections[collectionId];
    if (
      !collection ||
      !SalesToolkit.User.canSee(collection) ||
      !collection.hasOwnProperty("html")
    ) {
      loadedHeaderId = "";
      return;
    }

    let url = collection.html.url || "";
    if (!url.startsWith("http")) {
      loadedHeaderId = "";
      return;
    }

    loadedHeaderId = collectionId;

    $.ajax({
      url: url,
      type: "GET",
      success: function (data) {
        if (loadedHeaderId !== collectionId) return;

        const contentDoc = $header[0].contentWindow.document;
        contentDoc.open();
        contentDoc.write(data);
        contentDoc.close();

        // Inject the preloaded styles if available
        const preloadedCss = $("#articleCssPreload").html();
        if (preloadedCss) {
          const styleTag = contentDoc.createElement("style");
          styleTag.type = "text/css";
          styleTag.appendChild(document.createTextNode(preloadedCss));
          contentDoc.head.appendChild(styleTag);
        }

        setTimeout(() => {
          $header[0].style.height =
            contentDoc.documentElement.scrollHeight + "px";
        }, 100);

        $header.show();
      },
      error: function (data) {
        loadedHeaderId = "";
        $header.empty();
        $header.css("height", 0);
      },
    });
  }
  //#endregion

  //#region Filters
  function _initFilters() {
    let filters = SalesToolkit.Filters.getFilters();
    if (
      filters &&
      filters.length > 0 &&
      SalesToolkit.Theme.getBooleanProperty("useFilters", false)
    ) {
      _buildFilters();
      $(".filterdropdown").css("display", "flex");
      $(".filterdropdown").removeClass("disabled");
    } else {
      $(".filterdropdown").hide();
    }
  }

  function _updateFilters() {
    SalesToolkit.Filters.update({
      success: function (json) {
        _initFilters();
      },
      failure: function (reason, httpStatus, errorCode) {
        _initFilters();
      },
    });
  }
  /**
   * Sets up the UI display for when hidden filters are active.
   * @private
   */
  function _setupHiddenFiltersArea() {
    if (!hiddenFiltersCleanupRan) {
      hiddenFiltersCleanupRan = true;
      console.log("Setting up hidden filters area");
      _clearSearch();
      _calViewGoBack();
      collectionBrowser.setPath([]);
      _hideSearchBar();
    }

    hiddenFiltersCleanupRan = false;

    $("#hiddenFiltersTitle").html(
      SalesToolkit.Translation.getOrGetThemeKey(
        "hiddenFiltersTitle",
        "Hidden filters are applied"
      )
    );

    let filters = SalesToolkit.Filters.getFilters().filter(
      (x) =>
        Object.keys(SalesToolkit.Filters.activeFilters).contains(x.name) &&
        x.visible === false
    );
    let keyModifier = "";
    try {
      keyModifier = Object.entries(SalesToolkit.Filters.activeFilters)
        .filter((x) => filters.map((x) => x.name).contains(x[0]))
        .map((x) => x[1])
        .toString()
        .replaceAll(",", "");
    } catch (err) {
      console.error(err);
    }
    $("#hidden-filters-info").html(
      SalesToolkit.Translation.getOrGetThemeKey(
        "hiddenFiltersInfo" + keyModifier,
        SalesToolkit.Translation.getOrGetThemeKey("hiddenFiltersInfo", "")
      )
    );
    const filtersArea = $("#hidden-filters-panel .filters");
    filtersArea.empty();
    for (let i = 0; i < filters.length; i++) {
      let filter = filters[i];
      let label = _parent.getTranslatedLabel(filter);
      let selectedOption = _parent.getTranslatedLabel(
        filter.options.find(
          (x) => x.name === SalesToolkit.Filters.activeFilters[filter.name]
        )
      );

      filtersArea.append(
        `<div id="hiddenFilterWrapper"><h2>${label}</h2><span>${selectedOption}</span></div>`
      );
    }
  }

  /**
   * Clears all hidden filters and hides the specific UI area for them.
   * @private
   */
  function _clearHiddenFilters() {
    _clearAllFilters(false); // Calls the module's public clearAllFilters
    $("body").toggleClass("hiddenFilters", false);
    $("#hidden-filters-info").html("");
  }

  /**
   * Builds the filter UI from the available filter data. This is typically called
   * once the library has been loaded.
   * @private
   */
  function _buildFilters() {
    if ($("#filters-panel").length == 0) return;
    const filters = SalesToolkit.Filters.getFilters();
    if (!filters || typeof filters !== "object") return;

    $("#filters-panel .portlet-body.filters").empty();
    allFilteredDropdowns = []; // Uses module-scoped variable

    const options = {
      multiSelect: SalesToolkit.Theme.getBooleanProperty(
        "allowFiltersMultiSelection",
        allowFiltersMultiSelection
      ),
      showSelectionCount: SalesToolkit.Theme.getBooleanProperty(
        "filtersShowSelectionCount",
        true
      ),
      showZeroSelectionCount: SalesToolkit.Theme.getBooleanProperty(
        "filtersShowZeroSelectionCount",
        false
      ),
      showResults: SalesToolkit.Theme.getBooleanProperty(
        "filtersShowResults",
        true
      ),
      maxItems: SalesToolkit.Theme.getIntegerProperty(
        "filtersMaxItemsShown",
        15
      ),
      maxHeight: SalesToolkit.Theme.getIntegerProperty(
        "filtersMaxHeight",
        16 * 39 + 20
      ),
      allowSearchTerms: SalesToolkit.Theme.getBooleanProperty(
        "filtersAllowSearchTerms",
        true
      ),
      backgroundImagePadding: SalesToolkit.Theme.getIntegerProperty(
        "filterImagePadding",
        "24"
      ),
      itemPaddingTopBottom: SalesToolkit.Theme.getIntegerProperty(
        "filterItemPaddingTopBottom",
        "12"
      ),
      showClearAllButton: SalesToolkit.Theme.getBooleanProperty(
        "filtersShowClearButton",
        true
      ),
      hideEntriesWithZeroResults: SalesToolkit.Theme.getBooleanProperty(
        "filtersHideEntriesWithNoResults",
        true
      ),
      quickDeleteHeight: SalesToolkit.Theme.getIntegerProperty(
        "filterQuickDeleteHeight",
        30
      ),
      quickDeletePadding: SalesToolkit.Theme.getStringProperty(
        "filterQuickDeletePaddingText",
        "5px 10px"
      ),
      buttonTopBottomPadding: SalesToolkit.Theme.getIntegerProperty(
        "filterButtonTopBottomPadding",
        15
      ),
      ignoreMaxItemsWithSubOptions: SalesToolkit.Theme.getBooleanProperty(
        "filtersIgnoreMaxItemsWithSuboptions",
        true
      ),
      includeSubOptionsInMaxItems: SalesToolkit.Theme.getBooleanProperty(
        "filtersIncludeSubOptionsInMaxItems",
        false
      ),
      accordionDefaultOpen: SalesToolkit.Theme.getBooleanProperty(
        "filtersAccordionDefaultOpen",
        false
      ),
    };

    $("#filters-panel .portlet-body.filters-quick-delete")[0].innerHTML = "";
    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i];
      if (filter.hasOwnProperty("visible") && !filter.visible) {
        continue;
      }

      let $select = new FilteredDropdown(
        $("#filters-panel .portlet-body.filters")[0],
        $("#filters-panel .portlet-body.filters-quick-delete")[0],
        filter,
        options
      );
      // Call local handlers
      $select.onchange = _handleFilterChange;
      $select.onclearall = _onClearAll;

      $("#filters-panel .portlet-body.filters").append($select);
      allFilteredDropdowns.push($select);
    }

    const $clearAllButton =
      '<a style="padding-top:' +
      SalesToolkit.Theme.getIntegerProperty("clearAllButtonPaddingTop", 15) +
      'px;" "href="javascript:;" class="btn salestoolkit-blue" id="clearAllFiltersButton" data-action="clearAllFilters">' +
      SalesToolkit.Translation.get("filterClearAllOption", "Clear all") +
      "</a>";
    $("#filters-panel .portlet-body.filters").append($clearAllButton);

    if (
      window.location.href.indexOf("index.html") >= 0 ||
      window.location.pathname == "/"
    ) {
      let breadcrumbClicked = _getUrlParam("breadcrumb");
      if (breadcrumbClicked) {
        if (collectionBrowser) {
          collectionBrowser.clearFilters();
        }
        return;
      }
      _setupSelectForActiveFilters();
      _applyFilters();
      if (Object.keys(SalesToolkit.Filters.activeFilters || {}).length > 0) {
        $(".filters-row").show();
      }
    }
  }

  /**
   * Applies the currently active filters to the collection browser,
   * refreshing the displayed items.
   * @private
   */
  function _applyFilters() {
    if (!collectionBrowser) return;

    $("#collectionHeader").hide();

    let aopFilterName = SalesToolkit.Translation.get("aopFilterName", "AOP");

    if (Object.keys(SalesToolkit.Filters.activeFilters).length == 0) {
      collectionBrowser.clearFilters();
      // Uses module-scoped `loadedHeaderId` - the temporary global can now be removed.
      if (loadedHeaderId && loadedHeaderId.length > 0) {
        $("#collectionHeader").show();
      }

      if (
        (collectionBrowser.path.length > 0 &&
          collectionBrowser.activeTopLevelFilter !== aopFilterName) ||
        (typeof collectionBrowser.searchTerm !== "undefined" &&
          collectionBrowser.searchTerm !== "")
      )
        $("#topLevelFilters").hide();
      else {
        $("#topLevelFilters").show();
      }
      $("body").removeClass("filtering");
    } else {
      $("#topLevelFilters").hide();
      if (
        SalesToolkit.Theme.getBooleanProperty("goToTopLevelOnFilters", false)
      ) {
        collectionBrowser.setPath([]);
        calViewGoBack();
      }

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "showAppliedHiddenFiltersArea",
          false
        )
      ) {
        let activeFilters = SalesToolkit.Filters.activeFilters;
        let hasHiddenFiltersActive = false;
        for (const key in activeFilters) {
          if (Object.hasOwn(activeFilters, key)) {
            let filter = SalesToolkit.Filters.getFilters().find(
              (x) => x.name == key
            );
            if (filter?.visible === false) {
              hasHiddenFiltersActive = true;
              break;
            }
          }
        }

        $("body").toggleClass("hiddenFilters", hasHiddenFiltersActive);
        if (hasHiddenFiltersActive) {
          SalesToolkit.Filters.saveToStorage();
          _setupHiddenFiltersArea();
        }
      }

      collectionBrowser.filter();

      $("body").addClass("filtering");
      if (collectionBrowser.totalPages > 1) {
        document
          .querySelector(".collection-browser-pagination")
          .classList.add("shown");
      } else {
        document
          .querySelector(".collection-browser-pagination")
          .classList.remove("shown");
      }
    }

    if (collectionBrowser.filtered) {
      $("#topLevelFilters").css("display", "none");
    }

    if (
      !collectionBrowser.filtered &&
      (typeof collectionBrowser.searchTerm === "undefined" ||
        collectionBrowser.searchTerm === "") &&
      SalesToolkit.Theme.getBooleanProperty("useCalview", false) &&
      localStorage.calViewActive == "true"
    ) {
      $("body").addClass("showing-cal-view");
    } else {
      $("body").removeClass("showing-cal-view");
    }

    SalesToolkit.Filters.saveToStorage();
  }

  function _handleFilterChange(entryName, active, element, filteredDropdown) {
    if (filteredDropdown.selectedItems.length == 0) {
      _clearFilters(filteredDropdown);
      _parent.trackAppClick(
        $(filteredDropdown.clearAllButton),
        "div.clearAllButton",
        "trackingFiltersCleared",
        SalesToolkit.Translation.get(
          "trackingFiltersCleared",
          "trackingFiltersCleared"
        )
      );
    } else {
      if (active) {
        _parent.trackAppClick(
          $(element),
          "a." + entryName,
          "trackingFilterOptionSelected",
          SalesToolkit.Translation.get(
            "trackingFilterOptionSelected",
            "trackingFilterOptionSelected"
          )
        );
      } else {
        _parent.trackAppClick(
          $(element),
          "a." + entryName,
          "trackingFilterOptionRemoved",
          SalesToolkit.Translation.get(
            "trackingFilterOptionRemoved",
            "trackingFilterOptionRemoved"
          )
        );
      }
      _toggleActiveFilters(entryName, active, element, filteredDropdown);
    }
  }

  function _clearFilters(filteredDropdown) {
    delete SalesToolkit.Filters.activeFilters[filteredDropdown.filterData.name];
    filteredDropdown.clearAllSelected(false);
    _applyFilters();
  }

  function _onClearAll(filteredDropdown) {
    _clearFilters(filteredDropdown);
    _parent.trackAppClick(
      $(filteredDropdown.clearAllButton),
      "div.clearAllButton",
      "trackingFiltersCleared",
      SalesToolkit.Translation.get(
        "trackingFiltersCleared",
        "trackingFiltersCleared"
      )
    );
  }

  function _handleClearAllFilters(e) {
    _clearAllFilters(e?.detail?.hideFilters);
  }
  function _clearAllFilters(hideFilters = false) {
    if (hideFilters === true) {
      $(".filters-row").hide();
    }
    _parent.trackAppClick(
      $("#clearAllFiltersButton"),
      "#clearAllFiltersButton",
      "trackingFiltersClearAll",
      SalesToolkit.Translation.get(
        "trackingFiltersClearAll",
        "trackingFiltersClearAll"
      )
    );
    SalesToolkit.Filters.activeFilters = {};
    allFilteredDropdowns.forEach((fd) => fd.clearAllSelected(false));
    _applyFilters();
  }

  function _toggleFilters() {
    if ($(".filterdropdown").hasClass("disabled")) return false;

    $(".filters-row").toggle();
    if ($(".filters-row").is(":visible")) {
      _parent.trackAppClick(
        $(".filters-row"),
        ".filters-row",
        "trackingFiltersShow",
        SalesToolkit.Translation.get(
          "trackingFiltersShow",
          "trackingFiltersShow"
        )
      );
    } else {
      _parent.trackAppClick(
        $(".filters-row"),
        ".filters-row",
        "trackingFiltersHide",
        SalesToolkit.Translation.get(
          "trackingFiltersHide",
          "trackingFiltersHide"
        )
      );
    }
  }

  function _toggleActiveFilters(entryName, active, element, filteredDropdown) {
    let category = filteredDropdown.filterData.name;
    if (
      !SalesToolkit.Theme.getBooleanProperty(
        "allowFiltersMultiSelection",
        allowFiltersMultiSelection
      )
    ) {
      SalesToolkit.Filters.activeFilters[category] = entryName;
    } else {
      if (!SalesToolkit.Filters.activeFilters[category])
        SalesToolkit.Filters.activeFilters[category] = [];
      const index =
        SalesToolkit.Filters.activeFilters[category].indexOf(entryName);
      if (index > -1) {
        SalesToolkit.Filters.activeFilters[category].splice(index, 1);
        if (SalesToolkit.Filters.activeFilters[category].length == 0)
          delete SalesToolkit.Filters.activeFilters[category];
      } else {
        SalesToolkit.Filters.activeFilters[category].push(entryName);
      }
    }
    _applyFilters();
  }

  function _setupSelectForActiveFilters() {
    let keys = Object.keys(SalesToolkit.Filters.activeFilters);
    for (let i = 0; i < keys.length; i++) {
      let tagName = keys[i];
      let filteredDropdown = allFilteredDropdowns.find(
        (x) => x.filterData.name === tagName
      );
      if (!filteredDropdown) continue;
      let filters = SalesToolkit.Filters.activeFilters[tagName];
      if (!filters || filters.length == 0) continue;
      if (typeof filters === "string") filters = filters.split(",");
      filteredDropdown.setSelectedItems([...filters]);
    }
  }
  //#endregion

  //#region Helper functions
  function _getTopLevelSectionId() {
    if (collectionBrowser) {
      return collectionBrowser.topLevelSectionId;
    }
    return undefined;
  }

  function _getItemTemplate() {
    if (collectionBrowser) {
      return collectionBrowser.itemTemplate;
    }
    return undefined;
  }

  function _handleAddFormsToSideMenu() {
    if (!SalesToolkit.Theme.getBooleanProperty("showFormsInSideMenu", false)) {
      return;
    }

    let forms = [];
    const homeScreenForms = SalesToolkit.Forms.getHomeScreenForms();

    if (!collectionBrowser || collectionBrowser.path.length === 0) {
      forms = homeScreenForms;
    } else {
      const collectionId =
        collectionBrowser.path[collectionBrowser.path.length - 1];
      let collection = SalesToolkit.Library.collections[collectionId];
      forms = SalesToolkit.Forms.getFormsAssociatedWithCollection(collectionId);

      if (
        SalesToolkit.Theme.getBooleanProperty(
          "showAllParentCollectionForms",
          true
        )
      ) {
        while (collection && typeof collection.containedBy === "string") {
          collection = SalesToolkit.Library.collections[collection.containedBy];
          if (collection && SalesToolkit.User.canSee(collection)) {
            forms = SalesToolkit.Forms.getFormsAssociatedWithCollection(
              collection.id
            ).concat(forms);
          }
        }
      }
    }

    if (
      SalesToolkit.Theme.getBooleanProperty(
        "alwaysShowTopLevelFormsInIndexSideMenu",
        false
      )
    ) {
      forms = homeScreenForms.concat(forms);
    }

    _parent.renderFormsToSideMenu(forms);
  }

  function _handleApplyTheme() {
    if (!collectionBrowser) {
      return;
    }

    const homeCollectionId = SalesToolkit.getHomeCollectionID();

    if (
      homeCollectionId &&
      (!collectionBrowser.topLevelCollection ||
        collectionBrowser.topLevelCollection !== homeCollectionId)
    ) {
      collectionBrowser.topLevelCollection =
        SalesToolkit.User.startupCollection;
      collectionBrowser.setPath([], true);
    }

    if (collectionBrowser.path.length === 0) {
      const forceListView = SalesToolkit.Theme.getBooleanProperty(
        "forceListView_TopLevel",
        SalesToolkit.Theme.getBooleanProperty("forceListView", false)
      );
      const forceThumbView = SalesToolkit.Theme.getBooleanProperty(
        "forceThumbView_TopLevel",
        SalesToolkit.Theme.getBooleanProperty("forceThumbView", false)
      );

      if (forceListView) {
        $("body").toggleClass("showing-list-view", true);
      } else if (forceThumbView) {
        $("body").toggleClass("showing-list-view", false);
      }
    }
  }

  function _handlerResizeCalView(event) {
    const isFullScreen = event?.detail?.isFullScreen || false;
    _resizeCalView(isFullScreen);
  }
  function _handleActivateFilter(event) {
    if (event.detail && event.detail.filterName) {
      _activateFilter(event.detail.filterName);
    }
  }

  function _handleUpdateCartFavs(event) {
    if (event.detail && event.detail.item) {
      // The existing function handles the UI update
      _updateItemCartAndFavouritesTags(event.detail.item);
    }
  }

  function _handleLoadCollection(event) {
    if (event.detail && event.detail.collectionId) {
      // The existing function handles the UI update
      collectionBrowser?.viewCollection(event.detail.collectionId);
    }
  }

  function _handleSetKioskMode() {
    if (collectionBrowser && collectionBrowser.path.length > 0) {
      _parent.setKioskModeForCollection(
        collectionBrowser.path[collectionBrowser.path.length - 1]
      );
    }
  }

  function _handleCBRefresh(event) {
    if (!collectionBrowser) return;

    if (event && event.detail && typeof event.detail.sortMode !== "undefined") {
      collectionBrowser.sortMode = event.detail.sortMode;
    }

    collectionBrowser.refresh();
  }

  function _handleCBSetPath(event) {
    if (collectionBrowser && event.detail) {
      const path = event.detail.path;
      // Check for the ignoreTracking boolean, default to false if not provided
      const ignoreTracking = event.detail.ignoreTracking || false;
      collectionBrowser.setPath(path, ignoreTracking);
    }
  }

  function _handleCBRefreshPath(event) {
    if (collectionBrowser && event.detail) {
      const ignoreTracking = event.detail.ignoreTracking || false;
      collectionBrowser.setPath(collectionBrowser.path, ignoreTracking);
    }
  }

  function _handleBannerClick() {
    _resetToHomeState(true);
  }

  function _resetToHomeState(fromBanner = false) {
    // Dispatch event to clear filters
    _clearAllFilters(false);

    // Call private functions for other resets
    _calViewGoBack();
    _clearSearch();

    // Directly call the method on the local collectionBrowser object
    if (collectionBrowser) {
      collectionBrowser.setPath([]);
    }

    if (fromBanner) {
      const bannerImg = document.querySelector(".banner-img");
      // Call the global function and pass the jQuery element as expected
      _parent.trackAppClick(
        bannerImg,
        ".banner-img",
        "trackingIndexBannerClick",
        SalesToolkit.Translation.get(
          "trackingIndexBannerClick",
          "trackingIndexBannerClick"
        )
      );
      return;
    }
    // Perform analytics tracking
    _parent.trackAppClick(
      $("#indexHomeButton"), // This ID should be on your home button link
      "#indexHomeButton",
      "trackingIndexHomeClick",
      SalesToolkit.Translation.get(
        "trackingIndexHomeClick",
        "trackingIndexHomeClick"
      )
    );
  }

  /**
   * Central click handler for all actions within the index module.
   * Uses event delegation to capture clicks on elements with a `data-action` attribute.
   * @private
   */
  function _handleActionClick(event) {
    const target = event.target;
    const actionButton = target.closest("[data-action]");

    if (!actionButton) return;

    const action = actionButton.dataset.action;
    const isHome = actionButton.dataset.isHome === "true"; // For breadcrumb clicks
    const collectionId = actionButton.dataset.collectionId; // For breadcrumb clicks
    const journalId = actionButton.dataset.journalId;
    const filterName = actionButton.dataset.filterName;
    const pageNum = actionButton.dataset.pageNumber;
    const tooltips = document.querySelectorAll("body > .tooltip");
    // A switch statement is a clean way to handle multiple actions.
    switch (action) {
      case "clearAllFilters":
        _clearAllFilters(false);
        break;
      case "clearHiddenFilters":
        _clearHiddenFilters();
        break;
      case "breadcrumbClick":
        if (collectionId) collectionBrowser?.viewCollection(collectionId);
        _breadcrumbClick(actionButton, isHome);
        break;
      case "goBack":
        _onHomePageBackButtonClick();
        break;
      case "viewHome":
        _calViewGoBack();
        _clearSearch();
        collectionBrowser.setPath([]);
        break;
      case "activateJournal":
        if (journalId) {
          _activateJournalWithId(journalId);
        }
        break;
      case "toggleFullscreen":
        // This logic is moved from the old listener
        $(actionButton).tooltip("destroy").tooltip({ placement: "top" });

        for (let i = 0; i < tooltips.length; i++) {
          $(tooltips[i]).remove();
        }
        break;
      case "activateFilter":
        if (filterName) {
          _activateFilter(filterName);
        }
        break;
      case "viewPage":
        if (pageNum) {
          collectionBrowser.viewPage(pageNum);
        }
        break;
    }
  }

  function _handleLoadArticleRequest() {
    if (loadedHeaderId) {
      _loadArticleHeader(loadedHeaderId);
    }
  }

  /**
   * Removes login-specific parameters from a query string or object.
   * @private
   * @param {string|object} input - The query string or URLSearchParams object.
   * @returns {string|object} The cleaned query string or object.
   */
  function _cleanLoginURLParams(input) {
    const keysToRemove = [
      "userJSON",
      "apikey",
      "userName",
      "baseURL",
      "serverURL",
    ];

    if (typeof input === "string") {
      const query = input.startsWith("?") ? input.slice(1) : input;
      const params = new URLSearchParams(query);

      keysToRemove.forEach((key) => params.delete(key));

      return params.toString() ? "?" + params.toString() : "";
    }

    if (typeof input === "object" && input !== null) {
      // This handles URLSearchParams objects directly
      const cleanedParams = new URLSearchParams(input);
      keysToRemove.forEach((key) => cleanedParams.delete(key));
      return cleanedParams;
    }

    throw new Error(
      "Invalid input: expected query string or URLSearchParams object"
    );
  }
  //#endregion

  //#region Public Methods
  /**
   * Initializes the index module. This function is called when the index page is loaded.
   */
  function _init(parent) {
    console.log("Initializing indexModule...");
    _parent = parent;
    const stk = parent.getSalesToolkit();
    if (stk) SalesToolkit = stk;
    // --- Initialize Module Variables ---
    treeViewLargeCols =
      SalesToolkit.Theme.getIntegerProperty("treeViewPanelLargeCols", 2) +
      SalesToolkit.Theme.getIntegerProperty("treeViewExtraSpaceLargeCols", 1);
    treeViewMediumCols =
      SalesToolkit.Theme.getIntegerProperty("treeViewPanelMediumCols", 3) +
      SalesToolkit.Theme.getIntegerProperty("treeViewExtraSpaceMediumCols", 1);
    treeViewSmallCols =
      SalesToolkit.Theme.getIntegerProperty("treeViewPanelSmallCols", 0) +
      SalesToolkit.Theme.getIntegerProperty("treeViewExtraSpaceSmallCols", 0);
    main = document.getElementById("main-panel");
    tree = document.getElementById("tree-view-panel");
    searchBox = document.getElementById("search");
    searchClear = document.getElementById("searchclear");
    pluginBackEnabled = _getUrlParam("pluginBack", false);
    librarySyncInProgress = true;
    _initializeIconVariables();
    _preloadArticleHeaderCss();

    // --- Event Listeners ---
    const wrapper = document.getElementById("indexWrapper");
    if (wrapper) {
      wrapper.addEventListener("click", _handleActionClick);
    }

    _eventHandlerMap = {
      [_parent.Events.LOAD_MEETINGS]: _showCurrentCollectionMeetingScreen,
      [_parent.Events.RENDER_PLUGINS]: _renderCollectionBrowserPlugins,
      [_parent.Events.TOGGLE_SEARCH]: _toggleSearchBarVisibility,
      [_parent.Events.CLEAR_SEARCH]: _clearSearch,
      [_parent.Events.LOAD_ARTICLE]: _handleLoadArticleRequest,
      [_parent.Events.TOGGLE_TREEVIEW]: _toggleTree,
      [_parent.Events.CALVIEW_BACK]: _calViewGoBack,
      [_parent.Events.LIBRARY_LOADED]: _onLibraryLoaded,
      [_parent.Events.INIT_FILTERS]: _initFilters,
      [_parent.Events.UPDATE_FILTERS]: _updateFilters,
      [_parent.Events.CLEAR_ALL_FILTERS]: _handleClearAllFilters,
      [_parent.Events.APPLY_FILTERS]: _applyFilters,
      [_parent.Events.TOGGLE_FILTERS]: _toggleFilters,
      [_parent.Events.RESET_HOME]: _resetToHomeState,
      [_parent.Events.ACTIVATE_FILTER]: _handleActivateFilter,
      [_parent.Events.UPDATE_CART_FAVS]: _handleUpdateCartFavs,
      [_parent.Events.COLLECTION_BROWSER_LOAD_COLLECTION]:
        _handleLoadCollection,
      [_parent.Events.COLLECTION_BROWSER_SETPATH]: _handleCBSetPath,
      [_parent.Events.COLLECTION_BROWSER_REFRESH]: _handleCBRefresh,
      [_parent.Events.COLLECTION_BROWSER_REFRESH_PATH]: _handleCBRefreshPath,
      [_parent.Events.CALVIEW_RESIZE]: _handlerResizeCalView,
      [_parent.Events.APPLY_THEME]: _handleApplyTheme,
      [_parent.Events.ADD_FORMS_TO_SIDE_MENU]: _handleAddFormsToSideMenu,
      [_parent.Events.SET_KIOSK_MODE]: _handleSetKioskMode,
      [_parent.Events.BANNER_CLICKED]: _handleBannerClick,
    };

    for (const eventName in _eventHandlerMap) {
      if (Object.hasOwn(_eventHandlerMap, eventName)) {
        document.addEventListener(eventName, _eventHandlerMap[eventName]);
      }
    }

    $(document.body).on("click.indexModule", function (e) {
      if (keepSearchTagsOpen) {
        keepSearchTagsOpen = false;
        return;
      }

      const container = $(".searchdropdown");
      const autocompleteList = $(".autocomplete-items");

      // If the click is outside BOTH the search trigger and the results list...
      if (
        $(e.target).closest(container).length === 0 &&
        $(e.target).closest(autocompleteList).length === 0
      ) {
        // ...and the search box is empty, hide the bar (which also closes the list).
        if (!searchBox || searchBox.value.length === 0) {
          _hideSearchBar();
        } else {
          // ...otherwise, if the search box has text, just close the list.
          _closeAllLists();
        }
      }
    });

    $(document).on(
      "click.indexAutocomplete",
      ".autocomplete-items",
      function (e) {
        keepSearchTagsOpen = false;
        _closeAllLists(e.target);
      }
    );

    $("#main-div-center").on("scroll.indexModule", function () {
      if (!searchBox || searchBox.value.length == 0) _hideSearchBar();
    });

    if (searchClear) {
      $(searchClear).on("click.indexModule", _clearSearchAndHideSearchBar);
    }

    if (searchBox) {
      _autocomplete(searchBox, SalesToolkit.Filters.getSearchTerms());

      $(searchBox).on("keydown.indexModule", function (event) {
        if (event.which == "13" || event.which == 27) {
          if ($("#searchautocomplete-list .autocomplete-active").length > 0)
            return false;
          if ($("#searchautocomplete-list div").length == 1) {
            $("#searchautocomplete-list div").eq(0).click();
            return false;
          }
          searchBox.blur();
          event.preventDefault();
          if (searchBox.value.length == 0) _hideSearchBar();
          return false;
        }
      });
      $(searchBox).on(
        "input.indexModule change.indexModule",
        _executeSearchAfterShortDelay
      );
    }

    if (SalesToolkit.CrossPlatform.enabled) {
      $("#indexSyncButton")
        .on("touchstart.indexModule", _handleSyncPressStart)
        .on("touchend.indexModule", _handleSyncPressEnd);
    }

    // --- Initial State Setup ---
    _setupColumns();
    if (localStorage.hideTree) {
      _toggleTree();
    }

    //This was being called later on  _setupCalViewTabs();
    _setupSearchFromParam();
    _setupCollectionBrowserAndFilters();

    window.collectionBrowser = collectionBrowser;

    _setupTreeView();
    _setupCalViewTabs();
    _setupLibraryUpdates();
    _setupNewsTicker();
    _setupFinalCallbacksAndTracking();

    const unsyncedCount = SalesToolkit.Offline.unsyncedActions
      ? SalesToolkit.Offline.unsyncedActions.filter(function (x) {
          return x.name == "shareCart" || x.name == "submitForm";
        }).length
      : 0;

    if (
      unsyncedCount > 0 &&
      SalesToolkit.Theme.getBooleanProperty(
        "showOfflineActionsNotificiation",
        false
      )
    ) {
      const message = SalesToolkit.Translation.getFormatted(
        "offlineActionsMessage",
        "There are some offline actions that need to be synced to the server. Offline actions: {0}.",
        [unsyncedCount]
      );
      const title = SalesToolkit.Translation.get(
        "offlineActionsTitle",
        "Attention"
      );
      SalesToolkit.UI.Notifications.showCustomNotification(
        "info",
        message,
        title
      );
    }
    if (SalesToolkit.CrossPlatform.enabled) {
      SalesToolkit.CrossPlatform.SetStatubarStyle(
        SalesToolkit.Theme.getStringProperty("mainStatusBarStyle", "white")
      );
    }
  }

  /**
   * Destroys the index module. This function is called when navigating away from the index page.
   * It cleans up objects and event listeners to prevent memory leaks.
   */
  function _destroy() {
    console.log("Destroying indexModule...");

    _updateTickerCallback = undefined;

    // Clean up event listeners
    const wrapper = document.getElementById("indexWrapper");
    if (wrapper) {
      wrapper.removeEventListener("click", _handleActionClick);
    }

    if (_eventHandlerMap) {
      for (const eventName in _eventHandlerMap) {
        if (Object.hasOwn(_eventHandlerMap, eventName)) {
          document.removeEventListener(eventName, _eventHandlerMap[eventName]);
        }
      }
      // Clean up the map itself to be tidy.
      _eventHandlerMap = null;
    }

    $(document.body).off(".indexModule");
    $("#main-div-center").off(".indexModule");
    if (searchClear) $(searchClear).off(".indexModule");
    if (searchBox) $(searchBox).off(".indexModule");
    $("#indexSyncButton").off(".indexModule");
    $(document).off("click.indexAutocomplete");

    // Clean up components
    if (collectionBrowser && typeof collectionBrowser.destroy === "function") {
      collectionBrowser.destroy();
    }
    delete window.collectionBrowser;

    if (treeView && typeof treeView.destroy === "function") {
      treeView.destroy();
    } else if (treeView) {
      $("atheneum-tree-view").off(".jstree");
      $("atheneum-tree-view").jstree("destroy");
    }

    if (salestoolkitFirstTimeSyncInterval) {
      clearInterval(salestoolkitFirstTimeSyncInterval);
    }

    if (executeSearchTimeout) {
      clearTimeout(executeSearchTimeout);
    }
    if (pressTimer) {
      clearTimeout(pressTimer);
    }

    if (callLibraryReadyTimeout) {
      clearTimeout(callLibraryReadyTimeout);
    }

    if (hideSyncModalTimeout) {
      clearTimeout(hideSyncModalTimeout);
    }

    if (checkInitialSyncTimeout) {
      clearTimeout(checkInitialSyncTimeout);
    }
  }

  const _templateHTML = `
    <!-- BEGIN INDEX CONTAINER -->
        <!-- BEGIN CONTENT -->
        <div  id="indexWrapper" class="page-content-wrapper">
            <div id="main-div-center" class="page-content">
                <!-- BEGIN SAMPLE PORTLET CONFIGURATION MODAL FORM-->
                <div class="modal fade" id="portlet-config" tabindex="-1"
                     role="dialog" aria-labelledby="myModalLabel" aria-hidden="true">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                                <button type="button" class="close" data-dismiss="modal"
                                        aria-hidden="true"></button>
                                <h4 class="modal-title">
                                    <span class="title">
                                        <span class='hasTranslationKey'
                                              data-translation-key="modalTitle"
                                              data-translation-default='Modal Title'>Modal title</span>
                                </h4>
                            </div>
                            <div class="modal-body">
                                <span class="title">
                                    <span class='hasTranslationKey'
                                          data-translation-key="widgetSettings"
                                          data-translation-default='Widget setting form goes here'>
                                        Widget
                                        settings form goes here
                                    </span>
                            </div>
                            <div class="modal-footer">
                                <button type="button" class="btn blue">
                                    <span class="title">
                                        <span class='hasTranslationKey'
                                              data-translation-key="saveChanges"
                                              data-translation-default='Save Changes'>Save changes</span>
                                </button>
                                <button type="button" class="btn default" data-dismiss="modal">
                                    <span class="title">
                                        <span class='hasTranslationKey'
                                              data-translation-key="close" data-translation-default='Close'>Close</span>
                                </button>
                            </div>
                        </div>
                        <!-- /.modal-content -->
                    </div>
                    <!-- /.modal-dialog -->
                </div>
                <!-- /.modal -->
                <!-- END SAMPLE PORTLET CONFIGURATION MODAL FORM-->
                 <!-- BEGIN FILTERS-->
                 <div class="row filters-row">
                    <div id="filters-panel" class="col-md-12">
                        <div class="sidePadding10 portlet light">
                            <div class="portlet-title">
                                <strong>
                                    <span class="hasTranslationKey" data-translation-key="filterPanelTitle" data-translation-default="Filters">Filters</span>
                                </strong>
                            </div>
                            <div style="padding-top: 1px !important;" class="portlet-body filters">
                            </div>
                        </div>
                        <div class="sidePadding10 portlet hidden light">
                            <div style="padding-top: 1px !important;" class="portlet-body filters-quick-delete">
                            </div>
                        </div>
                    </div>
                </div>
                <!-- END FILTERS-->
                  <!-- BEGIN HIDDEN FILTERS-->
                 <div class="hidden-filters-row">
                    <div id="hidden-filters-panel" class="col-md-12">
                        <div>
                            <h1 id="hiddenFiltersTitle" class=""></h1>
                            <span id="hidden-filters-info"></span>
                            <div style="padding-top: 1px !important;" class="filters">
                            </div>
                            <button data-action="clearHiddenFilters" type="button" class="clearAllButton btn btn-primary" data-dismiss="modal">
                                    <span class="title">
                                        <span class='hasTranslationKey'
                                              data-translation-key="clearAll" data-translation-default='CLEAR ALL'>CLEAR ALL</span>
                                </button>
                        </div>
                    </div>
                </div>
                <!-- END HIDDEN FILTERS-->
                <div class="page-title">
                    <ul class="page-breadcrumb" style="padding-left:19px;" id="salestoolkit-breadcrumbs">
                        <li>
                            <a href="javascript:;" data-action="viewHome">
                                <span class='hasTranslationKey' data-translation-key='homeButton'
                                      data-translation-default="Home">Home </span>
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="page-bar news-ticker-page-bar">
                    <div class="page-toolbar pull-left news-ticker-toolbar">
                        <div id="salestoolkit-news-ticker-wrapper"></div>
                    </div>
                </div>
                <!-- END PAGE HEADER-->
                <!-- BEGIN PAGE CONTENT-->
                <div class="row" id="main-panel-wrapper">
                    <div id="main-panel" class="col-lg-12 col-md-12 col-sm-12 salestoolkit-main-panel">
                        <div class="hidden" id="page-calview-tabs">
                        </div>
                        <div class="sidePadding10 portlet light">
                            <div class="portlet-title">
                                <div class="caption">
                                    <strong>
                                        <span id="homekey">Home</span>
                                    </strong>
                                </div>
                                <div id="topLevelFilters" style="display:none;">
                                </div>
                                <div class="actions" style="display: flex;">
                                    <div id="sort-action-wrapper" style="display:none" class="view-toggler">
                                        <img id="default-sort-icon" 
                                        data-original-title="Default Sorting"
                                        data-tooltip-translationkey="sortingDefault" 
                                        data-tooltip-translation-default="Default Sorting"/>
                                        <img id="az-sort-icon" style="display:none" 
                                        data-original-title="A-Z Sorting"
                                        data-tooltip-translationkey="sortingAZ" 
                                        data-tooltip-translation-default="A-Z Sorting"/>
                                        <img id="za-sort-icon" style="display:none" 
                                        data-original-title="Z-A Sorting"
                                        data-tooltip-translationkey="sortingZA"
                                        data-tooltip-translation-default="Z-A Sorting" />
                                    </div>
                                    <div class="view-toggler"  >
                                        <img id="list-view-icon" 
                                        data-original-title="List View"
                                        data-tooltip-translationkey="toggleViewList"
                                        data-tooltip-translation-default="List View">
                                        <img id="thumbnail-icon" 
                                        data-original-title="Thumb View"
                                        data-tooltip-translationkey="toggleViewThumb"
                                        data-tooltip-translation-default="Thumb View">
                                    </div>
                                    <div id="calendarViewToggle" class="view-toggler"  >
                                        <img id="calendar-icon-on" 
                                        data-original-title="Calendar View Off"
                                        data-tooltip-translationkey="toggleCalendarViewOff"
                                        data-tooltip-translation-default="Calendar View Off">
                                        <img id="calendar-icon-off" 
                                        data-original-title="Calendar View On"
                                        data-tooltip-translationkey="toggleCalendarViewOn"
                                        data-tooltip-translation-default="Calendar View On">
                                    </div>
                                    <div style="display:none" class="toggle-switch-container">
                                        <div class="toggle-switch switch-vertical">
                                          <input id="toggle-saved-cart" class="no-uniform" type="radio" name="toggle-cart-switch" value="1" checked="checked" />
                                          <label for="toggle-saved-cart"></label>
                                          <input id="toggle-current-cart" class="no-uniform" type="radio" name="toggle-cart-switch" value="2" />
                                          <label data-translation-key="currentCartOption" data-translation-default="Current Cart" for="toggle-current-cart">Current Cart</label>
                                          <span class="toggle-outside">
                                            <span class="toggle-inside"></span>
                                          </span>
                                        </div>
                                      </div>
                                    <a class="btn btn-circle btn-icon-only btn-default fullscreen" data-action="toggleFullscreen"
                                       href="javascript:;">
                                        <img class="zoomin-icon"
                                        data-original-title="Full Screen"
                                       data-tooltip-translationkey="fullscreen"
                                       data-tooltip-translation-default="Full Screen">
                                        <img class="zoomout-icon"
                                        data-original-title="Minimise"
                                       data-tooltip-translationkey="minimise"
                                       data-tooltip-translation-default="Minimise">
                                    </a>
                                </div>
                            </div>
                            <div style="padding-top: 1px !important;" class="portlet-body home">
                                <div id="breadcrumbsbuttonhide" class="breadcrumbsBackButton">
                                    <a type="button" data-action="goBack">
                                        <img class="backButtonIcon" width="20px" height="20px">
                                        <span style="font-weight: 600; font-size: 14px; margin-left: 4px;" data-translation-key="back" data-translation-default="Back">Back</span>
                                    </a>
                                </div>
                                <div style="display: none;" id="sortby-item" class="sortby-title home-sortby">
                                    <span id="sortby-name" class="sortby-head">Sort By</span>
                                    <div class="sortby-options-container">
                                        <ul class="sortby-options home-sortby"></ul>
                                    </div>
                                </div>
                                <iframe class="" id="collectionHeader" frameborder="0" scrolling="no" >
                                </iframe>
                                <iframe class="row" scrolling="no" id="atheneum-calview-ui"></iframe>
                                <div class="row" id="atheneum-home-page"></div>
                                <div class="collection-browser-pagination"></div>
                            </div>
                        </div>
                    </div>
                    <div id="tree-view-panel" class="col-sm-0 col-md-0 col-lg-0" style="display: block;">
                        <div class="portlet light">
                            <div class="portlet-title">
                                <div class="caption">
                                    <strong>
                                        <span class='hasTranslationKey' data-translation-key='treeView'
                                              data-translation-default='Tree View'>Tree View</span>
                                    </strong>
                                </div>
                                <div class="actions">
                                    <a class="btn btn-circle btn-icon-only btn-default fullscreen" data-action="toggleFullscreen"
                                       href="javascript:;">
                                        <img class="zoomin-icon"
                                        data-original-title="Full Screen" 
                                       data-tooltip-translationkey="fullscreen"
                                       data-tooltip-translation-default="Full Screen">
                                        <img class="zoomout-icon"
                                        data-original-title="Minimise"
                                       data-tooltip-translationkey="minimise"
                                       data-tooltip-translation-default="Minimise">
                                    </a>
                                </div>
                            </div>
                            <div class="portlet-body home">
                                <div id="atheneum-tree-view"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- END PAGE CONTENT-->
        </div>
    <!-- END INDEX CONTENT -->
    `;

  // Expose the public init and destroy methods.
  return {
    init: _init,
    destroy: _destroy,
    templateHTML: _templateHTML,
  };

  //#endregion
})();
